import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import {
  Package, ListOrdered, BarChart3, Boxes, Loader2, Search, ChevronDown,
  ChevronUp, RefreshCw, Plus, Truck, Warehouse, Layout, Users, Trash2,
  Download, FileText, RotateCcw, ArrowRight,
  CreditCard, AlertTriangle, ScrollText, ToggleLeft, ToggleRight,
  Upload, Tag, GripVertical, EyeOff, Eye, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useAdminFetch } from "@/hooks/useAdminFetch";
import { useCatalog, isHiddenById } from "@/context/CatalogContext";
import AdminProductForm from "@/components/admin/AdminProductForm";
import BulkUploadModal from "@/components/admin/BulkUploadModal";
import AdminShell from "@/components/admin/AdminShell";
import { resolveAssetUrl } from "@/lib/assetUrl";
import StoreImg from "@/components/store/StoreImg";
import { useAdminI18n } from "@/lib/adminI18n";
import {
  ADMIN_PLACEHOLDER,
  extractApiMessage,
  extractCustomerIdentity,
  extractOrderCustomer,
  formatLanguageLabel,
  formatAdminDate,
  readJsonSafe,
  verificationLabel,
  withPlaceholder,
} from "@/lib/adminDisplay";
import { ALL_PRODUCTS, localized, formatPrice, computeDiscountedPrice, getProductDiscountPct } from "@/data/products";

const STATUSES = ["new", "confirmed", "delivered", "cancelled"];

const STATUS_STYLES = {
  new: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  confirmed: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  delivered: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-red-500/10 text-red-400 border-red-500/30",
};

const DASHBOARD_TABS = [
  { id: "orders", labelKey: "orders", fallback: "Orders", icon: ListOrdered, permission: "orders.manage", description: "Track checkout flow, status updates, and export activity." },
  { id: "customers", labelKey: "orderCustomers", fallback: "Order Customers", icon: Users, permission: "customers.manage", description: "Review customer histories tied to orders and phone activity." },
  { id: "trash", labelKey: "trash", fallback: "Trash", icon: Trash2, permission: "orders.manage", description: "Restore soft-deleted orders and review cancellations with reasons." },
  { id: "stock", labelKey: "stock", fallback: "Stock", icon: Boxes, permission: "products.manage", description: "Manage quantity overlays and live storefront inventory." },
  { id: "products", labelKey: "products", fallback: "Products", icon: Package, permission: "products.manage", description: "Create, edit, hide, and bulk import the store catalog." },
  { id: "shein-categories", labelKey: "shein", fallback: "SHEIN", icon: Tag, permission: "products.manage", description: "Organize storefront SHEIN categories and visibility." },
  { id: "missing-data", labelKey: "missingData", fallback: "Missing Data", icon: AlertTriangle, permission: "products.manage", description: "Catch incomplete products before they reach customers." },
  { id: "payments", labelKey: "payments", fallback: "Payments", icon: CreditCard, permission: "settings.manage", description: "Enable or disable checkout payment methods safely." },
  { id: "logs", labelKey: "logs", fallback: "Logs", icon: ScrollText, permission: "audit.view", description: "Inspect append-only admin activity records." },
  { id: "analytics", labelKey: "analytics", fallback: "Analytics", icon: BarChart3, permission: "audit.view", description: "Read order and revenue trends without leaving admin." },
];

const TAB_ALIASES = {
  shein: "shein-categories",
  missing: "missing-data",
};

const EVENT_LABELS = {
  page_view: { en: "Page view", ar: "عرض صفحة" },
  home_view: { en: "Home page view", ar: "عرض الصفحة الرئيسية" },
  store_view: { en: "Store view", ar: "عرض المتجر" },
  product_detail_view: { en: "Product detail view", ar: "عرض تفاصيل المنتج" },
  shein_view: { en: "SHEIN page view", ar: "عرض صفحة SHEIN" },
  country_page_view: { en: "Country page view", ar: "عرض صفحة دولة" },
  shipping_rates_view: { en: "Shipping rates view", ar: "عرض أسعار الشحن" },
  shipping_only_view: { en: "Shipping Only view", ar: "عرض صفحة الشحن فقط" },
  warehouses_view: { en: "Warehouses view", ar: "عرض المستودعات" },
  login_view: { en: "Login page view", ar: "عرض صفحة الدخول" },
  signup_view: { en: "Signup page view", ar: "عرض صفحة التسجيل" },
  product_view: { en: "Product view", ar: "عرض منتج" },
  product_search: { en: "Product search", ar: "بحث عن منتج" },
  product_filter: { en: "Product filter", ar: "تصفية المنتجات" },
  product_sort: { en: "Product sort", ar: "ترتيب المنتجات" },
  product_add_to_cart_click: { en: "Add to cart clicked", ar: "ضغط إضافة إلى السلة" },
  product_add_to_cart_success: { en: "Added to cart", ar: "تمت الإضافة إلى السلة" },
  product_add_to_cart_failed: { en: "Add to cart failed", ar: "فشل الإضافة إلى السلة" },
  cart_view: { en: "Cart view", ar: "عرض السلة" },
  checkout_start: { en: "Checkout started", ar: "بدء الدفع" },
  checkout_submit_attempt: { en: "Checkout submitted", ar: "إرسال الدفع" },
  checkout_success: { en: "Checkout completed", ar: "اكتمل الدفع" },
  checkout_failed: { en: "Checkout failed", ar: "فشل الدفع" },
  shipping_calculator_view: { en: "Shipping calculator view", ar: "عرض حاسبة الشحن" },
  shipping_calculator_calculate: { en: "Shipping calculation", ar: "احتساب الشحن" },
  shipping_country_select: { en: "Shipping country selected", ar: "اختيار دولة الشحن" },
  shipping_method_select: { en: "Shipping method selected", ar: "اختيار طريقة الشحن" },
  shipping_only_open: { en: "Shipping Only opened", ar: "فتح الشحن فقط" },
  warehouse_copy_address: { en: "Warehouse address copied", ar: "نسخ عنوان المستودع" },
  shipping_mark_copy: { en: "Shipping mark copied", ar: "نسخ علامة الشحن" },
  whatsapp_click: { en: "WhatsApp clicked", ar: "ضغط واتساب" },
  contact_click: { en: "Contact clicked", ar: "ضغط التواصل" },
  shop_and_ship_click: { en: "Shop & Ship clicked", ar: "ضغط Shop & Ship" },
  bob_store_click: { en: "BOB Store clicked", ar: "ضغط متجر BOB" },
  china_suppliers_section_view: { en: "China suppliers section view", ar: "عرض قسم موردي الصين" },
  china_suppliers_open_click: { en: "China supplier opened", ar: "فتح مورد صيني" },
  customer_login_success: { en: "Customer login succeeded", ar: "نجح دخول العميل" },
  customer_login_failed: { en: "Customer login failed", ar: "فشل دخول العميل" },
  customer_signup_success: { en: "Customer signup succeeded", ar: "نجح تسجيل العميل" },
  customer_signup_failed: { en: "Customer signup failed", ar: "فشل تسجيل العميل" },
  email_verification_requested: { en: "Verification email requested", ar: "طلب بريد التحقق" },
  email_verification_success: { en: "Email verified", ar: "تم التحقق من البريد" },
  password_reset_requested: { en: "Password reset requested", ar: "طلب إعادة تعيين كلمة المرور" },
};

const ACTION_LABELS = {
  "order.status.update": { en: "Order status changed", ar: "تم تغيير حالة الطلب" },
  "order.soft_delete": { en: "Order moved to trash", ar: "تم نقل الطلب إلى المهملات" },
  "order.restore": { en: "Order restored", ar: "تمت استعادة الطلب" },
  "order.cancel": { en: "Order cancelled", ar: "تم إلغاء الطلب" },
  "order.note.add": { en: "Private order note added", ar: "تمت إضافة ملاحظة داخلية للطلب" },
  "order.export.xlsx": { en: "Orders exported", ar: "تم تصدير الطلبات" },
  "settings.payment_methods.update": { en: "Payment methods updated", ar: "تم تحديث وسائل الدفع" },
  "staff.create": { en: "Staff account created", ar: "تم إنشاء حساب موظف" },
  "staff.update": { en: "Staff account updated", ar: "تم تحديث حساب موظف" },
  "staff.password.reset": { en: "Staff password reset", ar: "تمت إعادة تعيين كلمة مرور موظف" },
  "approval.create": { en: "Approval request created", ar: "تم إنشاء طلب موافقة" },
  "approval.approved": { en: "Approval request approved", ar: "تمت الموافقة على الطلب" },
  "approval.rejected": { en: "Approval request rejected", ar: "تم رفض طلب الموافقة" },
  "content.draft.save": { en: "Content draft saved", ar: "تم حفظ مسودة المحتوى" },
  "content.publish": { en: "Content published", ar: "تم نشر المحتوى" },
  "product.create": { en: "Product created", ar: "تم إنشاء منتج" },
  "product.update": { en: "Product updated", ar: "تم تحديث منتج" },
  "product.hide": { en: "Product hidden", ar: "تم إخفاء منتج" },
  "product.restore": { en: "Product restored", ar: "تمت استعادة منتج" },
  "stock.update": { en: "Stock updated", ar: "تم تحديث المخزون" },
};

const FIELD_LABELS = {
  status: { en: "Status", ar: "الحالة" },
  hidden: { en: "Hidden", ar: "مخفي" },
  deleted: { en: "Deleted", ar: "محذوف" },
  reason: { en: "Reason", ar: "السبب" },
  note: { en: "Note", ar: "ملاحظة" },
  role: { en: "Role", ar: "الدور" },
  permissions: { en: "Permissions", ar: "الصلاحيات" },
  email: { en: "Email", ar: "البريد الإلكتروني" },
  name: { en: "Name", ar: "الاسم" },
  source: { en: "Source", ar: "المصدر" },
  severity: { en: "Severity", ar: "الأهمية" },
  result: { en: "Result", ar: "النتيجة" },
  must_change_password: { en: "Must change password", ar: "يجب تغيير كلمة المرور" },
  password_reset_at: { en: "Password reset time", ar: "وقت إعادة تعيين كلمة المرور" },
  payment_method: { en: "Payment method", ar: "وسيلة الدفع" },
};

const STATUS_LABELS = {
  new: { en: "New", ar: "جديد" },
  confirmed: { en: "Confirmed", ar: "مؤكد" },
  delivered: { en: "Delivered", ar: "تم التسليم" },
  cancelled: { en: "Cancelled", ar: "ملغى" },
};

function localizedLabel(map, key, lang, fallback) {
  const entry = map[String(key || "").toLowerCase()] || map[String(key || "")];
  return entry?.[lang] || entry?.en || fallback;
}

function humanizeKey(value) {
  return String(value || "")
    .replace(/^\/+/, "")
    .replace(/[_.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase()) || ADMIN_PLACEHOLDER;
}

function friendlyEventName(value, lang) {
  return localizedLabel(EVENT_LABELS, value, lang, humanizeKey(value));
}

function friendlyActionName(value, lang) {
  return localizedLabel(ACTION_LABELS, value, lang, humanizeKey(value));
}

function friendlyStatus(value, lang) {
  return localizedLabel(STATUS_LABELS, value, lang, humanizeKey(value));
}

function friendlyFieldName(value, lang) {
  return localizedLabel(FIELD_LABELS, value, lang, humanizeKey(value));
}

function friendlyPathName(value, lang) {
  const raw = String(value || "").trim();
  if (!raw) return ADMIN_PLACEHOLDER;
  let path = raw.replace(/^https?:\/\/[^/]+/i, "").split("?")[0] || "/";
  if (path === "/") return lang === "ar" ? "الصفحة الرئيسية" : "Home page";
  const parts = path.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (parts.length === 0) return lang === "ar" ? "الصفحة الرئيسية" : "Home page";
  const first = parts[0];
  const maps = {
    store: { en: "Store", ar: "المتجر" },
    country: { en: "Country page", ar: "صفحة دولة" },
    "shipping-rates": { en: "Shipping rates", ar: "أسعار الشحن" },
    "shipping-only": { en: "Shipping Only", ar: "الشحن فقط" },
    warehouses: { en: "Warehouses", ar: "المستودعات" },
    cart: { en: "Cart", ar: "السلة" },
    checkout: { en: "Checkout", ar: "الدفع" },
    account: { en: "Customer account", ar: "حساب العميل" },
    login: { en: "Login", ar: "تسجيل الدخول" },
    signup: { en: "Signup", ar: "إنشاء حساب" },
  };
  const base = maps[first]?.[lang] || maps[first]?.en || humanizeKey(first);
  const rest = parts.slice(1).map(humanizeKey).join(" / ");
  return rest ? `${base}: ${rest}` : base;
}

function friendlyValue(value, lang) {
  if (value === null || value === undefined || value === "") return ADMIN_PLACEHOLDER;
  if (typeof value === "boolean") return value ? (lang === "ar" ? "نعم" : "Yes") : (lang === "ar" ? "لا" : "No");
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map((item) => friendlyValue(item, lang)).join(", ");
  if (typeof value === "object") {
    const display = value.name || value.title || value.label || value.email || value.id || value.order_number || value.status;
    return display ? friendlyValue(display, lang) : ADMIN_PLACEHOLDER;
  }
  const text = String(value);
  if (STATUS_LABELS[text]) return friendlyStatus(text, lang);
  return text;
}

function summarizeObject(value, lang) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value)
    .filter(([key]) => !["password", "password_hash", "token", "reset_token", "_id"].includes(String(key).toLowerCase()))
    .slice(0, 8)
    .map(([key, val]) => ({ label: friendlyFieldName(key, lang), value: friendlyValue(val, lang) }));
}

export default function AdminDashboardPage() {
  const { isAuthenticated, verified, hasPermission } = useAdminAuth();
  const { t } = useAdminI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = useMemo(
    () => DASHBOARD_TABS.filter((item) => hasPermission(item.permission)),
    [hasPermission],
  );
  const requestedTab = TAB_ALIASES[searchParams.get("tab")] || searchParams.get("tab") || "orders";
  const fallbackTab = visibleTabs[0]?.id || null;
  const tab = visibleTabs.some((item) => item.id === requestedTab) ? requestedTab : fallbackTab;
  const activeTabMeta = visibleTabs.find((item) => item.id === tab) || visibleTabs[0] || DASHBOARD_TABS[0];
  const ActiveTabIcon = activeTabMeta.icon;

  useEffect(() => {
    if (tab && requestedTab !== tab) {
      setSearchParams(tab === "orders" ? {} : { tab }, { replace: true });
    }
  }, [requestedTab, setSearchParams, tab]);

  if (verified === null) {
    return (
      <main className="min-h-screen bg-[#0F0F0F] text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </main>
    );
  }
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;

  const openTab = (id) => {
    if (!visibleTabs.some((item) => item.id === id)) return;
    setSearchParams(id === "orders" ? { tab: "orders" } : { tab: id });
  };

  const quickLinks = [
    {
      title: t("dashboard.ordersQueue"),
      body: t("dashboard.ordersQueueBody"),
      icon: ListOrdered,
      action: t("dashboard.ordersQueueAction"),
      permission: "orders.manage",
      onClick: () => openTab("orders"),
    },
    {
      title: t("dashboard.catalogControl"),
      body: t("dashboard.catalogControlBody"),
      icon: Package,
      action: t("dashboard.catalogControlAction"),
      permission: "products.manage",
      onClick: () => openTab("products"),
    },
    {
      title: t("dashboard.customers"),
      body: t("dashboard.customersBody"),
      icon: Users,
      action: t("dashboard.customersAction"),
      permission: "customers.manage",
      onClick: () => navigate("/admin/customers"),
    },
    {
      title: t("dashboard.shippingOps"),
      body: t("dashboard.shippingOpsBody"),
      icon: Truck,
      action: t("dashboard.shippingOpsAction"),
      permission: "content.manage",
      onClick: () => navigate("/admin/shipping"),
    },
  ].filter((item) => hasPermission(item.permission));

  const showContentShortcuts = hasPermission("content.manage");

  if (!tab) {
    return (
      <AdminShell
        title={t("dashboard.title")}
        description={t("dashboard.description")}
      >
        <div className="admin-panel px-6 py-10 text-center text-sm text-white/60">
          {t("admin.accessDenied", "Access denied")}
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={t("dashboard.title")}
      description={t("dashboard.description")}
    >
      <div className="space-y-6" data-testid="admin-dashboard">
        <section className="grid gap-4 xl:grid-cols-[1.25fr,1fr]">
          <article className="admin-panel px-5 py-5 sm:px-6 sm:py-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-2xl">
                <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#F58D4A]">
                  {t("dashboard.overview")}
                </div>
                <h2 className="mt-2 text-2xl font-semibold text-white sm:text-[2rem]">
                  {t("dashboard.overviewTitle")}
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-6 text-white/58">
                  {t("dashboard.overviewBody")}
                </p>
              </div>
              {showContentShortcuts && (
                <div className="grid w-full gap-3 sm:grid-cols-2 xl:w-[25rem]">
                  <button
                    type="button"
                    onClick={() => navigate("/admin/shipping")}
                    className="admin-panel-soft flex min-h-[6.2rem] flex-col items-start justify-between px-4 py-4 text-left transition hover:border-[#F37021]/30 hover:bg-white/[0.06]"
                  >
                    <Truck className="h-5 w-5 text-[#F58D4A]" />
                    <div>
                      <div className="text-sm font-semibold text-white">{t("dashboard.shippingRates")}</div>
                      <div className="mt-1 text-xs leading-5 text-white/45">{t("dashboard.shippingRatesBody")}</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/admin/content")}
                    className="admin-panel-soft flex min-h-[6.2rem] flex-col items-start justify-between px-4 py-4 text-left transition hover:border-[#F37021]/30 hover:bg-white/[0.06]"
                  >
                    <Layout className="h-5 w-5 text-[#F58D4A]" />
                    <div>
                      <div className="text-sm font-semibold text-white">{t("dashboard.content")}</div>
                      <div className="mt-1 text-xs leading-5 text-white/45">{t("dashboard.contentBody")}</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </article>

          <div className="grid gap-4 sm:grid-cols-2">
            {quickLinks.map(({ title: quickTitle, body, icon: Icon, action, onClick }) => (
              <button
                key={quickTitle}
                type="button"
                onClick={onClick}
                className="admin-panel flex min-h-[11rem] flex-col items-start justify-between px-5 py-5 text-left transition hover:border-[#F37021]/28 hover:bg-white/[0.06]"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F37021]/14 text-[#F58D4A]">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-base font-semibold text-white">{quickTitle}</div>
                  <p className="mt-2 text-sm leading-6 text-white/52">{body}</p>
                </div>
                <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#F7A76F]">
                  {action}
                  <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="admin-panel px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/35">
                {t("dashboard.workspace")}
              </div>
              <div className="mt-1 flex items-center gap-2 text-white">
                <ActiveTabIcon className="h-5 w-5 text-[#F58D4A]" />
                <h3 className="text-lg font-semibold">{t(`dashboard.${activeTabMeta.labelKey}`, activeTabMeta.fallback)}</h3>
              </div>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-white/52">
                {activeTabMeta.description}
              </p>
            </div>
            <div className="text-xs text-white/38">
              {t("dashboard.workspaceNote")}
            </div>
          </div>

          <nav className="admin-scroll-row mt-4 flex gap-2 overflow-x-auto pb-1">
            {visibleTabs.map(({ id, labelKey, fallback, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => openTab(id)}
                className={`inline-flex h-11 items-center gap-2 rounded-full border px-4 text-xs font-semibold transition whitespace-nowrap ${
                  tab === id
                    ? "border-[#F37021]/45 bg-[#F37021]/16 text-white shadow-[0_12px_30px_rgba(243,112,33,0.16)]"
                    : "border-white/10 bg-white/[0.04] text-white/65 hover:border-[#F37021]/28 hover:bg-white/[0.07] hover:text-white"
                }`}
                data-testid={`admin-tab-${id}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t(`dashboard.${labelKey}`, fallback)}
              </button>
            ))}
          </nav>
        </section>

        {tab === "orders" && <OrdersTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "trash" && <TrashTab />}
        {tab === "stock" && <StockTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "shein-categories" && <SheinCategoriesTab />}
        {tab === "missing-data" && <MissingDataTab />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "logs" && <LogsTab />}
        {tab === "analytics" && <AnalyticsTab />}
      </div>
    </AdminShell>
  );
}

// ============================================================================
// ORDERS
// ============================================================================
function OrdersTab() {
  const { apiFetch, token } = useAdminAuth();
  const { t, lang } = useAdminI18n();
  const statusUpdatedCopy = lang === "ar" ? "تم تحديثه إلى" : "updated to";
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [tick, setTick] = useState(0);
  const path = `/admin/orders${statusFilter ? `?status=${statusFilter}` : ""}`;
  const { data, loading, error } = useAdminFetch(apiFetch, path, [tick]);
  const [localOverrides, setLocalOverrides] = useState({});
  const [deletedOverrides, setDeletedOverrides] = useState(new Set());
  const reload = () => setTick((n) => n + 1);

  // Merge any in-session status updates onto the fetched list so the UI
  // reflects them without a full reload.
  const orders = useMemo(() => {
    const base = (data?.items || []).filter((o) => !deletedOverrides.has(o.order_number));
    if (Object.keys(localOverrides).length === 0) return base;
    return base.map((o) => localOverrides[o.order_number]
      ? { ...o, status: localOverrides[o.order_number] }
      : o
    );
  }, [data, localOverrides, deletedOverrides]);

  const updateStatus = async (orderNum, next) => {
    if (next === "cancelled") {
      // Prompt for cancellation reason ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â admin-only metadata.
      const reason = window.prompt("Cancellation reason (optional):", "");
      const res = await apiFetch(`/admin/orders/${orderNum}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason: reason || null }),
      });
      const payload = await readJsonSafe(res);
      if (res.ok) {
        toast.success(`Order ${orderNum} cancelled`);
        setLocalOverrides((p) => ({ ...p, [orderNum]: "cancelled" }));
      } else {
        toast.error(extractApiMessage(res, payload, "Could not cancel order"));
      }
      return;
    }
    const res = await apiFetch(`/admin/orders/${orderNum}/status`, {
      method: "PUT",
      body: JSON.stringify({ status: next }),
    });
    const payload = await readJsonSafe(res);
    if (res.ok) {
      toast.success(`Order ${orderNum} ${statusUpdatedCopy} ${next}`);
      setLocalOverrides((p) => ({ ...p, [orderNum]: next }));
    } else {
      toast.error(extractApiMessage(res, payload, "Could not update status"));
    }
  };

  const softDeleteOrder = async (orderNum) => {
    const reason = window.prompt(
      "Soft-delete reason (recommended). The order will move to Trash, not be permanently deleted.",
      ""
    );
    if (reason === null) return; // user pressed Cancel
    const res = await apiFetch(`/admin/orders/${orderNum}`, {
      method: "DELETE",
      body: JSON.stringify({ reason: reason || null }),
    });
    const payload = await readJsonSafe(res);
    if (res.ok) {
      toast.success(`Order ${orderNum} moved to Trash`);
      setDeletedOverrides((prev) => new Set(prev).add(orderNum));
    } else {
      toast.error(extractApiMessage(res, payload, "Could not move to trash"));
    }
  };

  const exportXlsx = async () => {
    const qs = statusFilter ? `?status=${statusFilter}` : "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders-export.xlsx${qs}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      toast.error("Export failed");
      return;
    }
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `bob-orders-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
    toast.success("Orders exported");
  };

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <div className="admin-panel px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="font-display text-2xl font-bold">{t("dashboard.ordersHeading")}</h2>
            <p className="mt-1 text-sm text-white/48">
              {t("dashboard.ordersBody")}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="admin-input h-10 px-3 text-xs"
            data-testid="orders-status-filter"
          >
            <option value="">{t("dashboard.allStatuses")}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" onClick={exportXlsx} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-bold" data-testid="orders-export-xlsx">
            <Download className="w-3.5 h-3.5" /> {t("dashboard.exportXlsx")}
          </button>
          <button type="button" onClick={reload} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/5 hover:bg-white/10 text-xs">
            <RefreshCw className="w-3.5 h-3.5" /> {t("dashboard.refresh")}
          </button>
        </div>
      </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState label={error} onRetry={reload} />
      ) : orders.length === 0 ? (
        <Empty label={t("dashboard.noOrdersYet")} />
      ) : (
        <div className="space-y-2">
          {orders.map((o) => {
            const customer = extractOrderCustomer(o);
            return (
              <article key={o.order_number} className="admin-panel-soft overflow-hidden" data-testid={`order-${o.order_number}`}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === o.order_number ? null : o.order_number)}
                className="w-full p-4 flex items-center justify-between gap-3 hover:bg-white/[0.02] transition-colors text-left"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-[#F37021]">{o.order_number}</span>
                    {customer.customerId && (
                      <span className="inline-flex items-center rounded-full border border-[#F37021]/25 bg-[#F37021]/10 px-2 py-0.5 text-[10px] font-bold tracking-wider text-[#F7A76F]">
                        {customer.customerId}
                      </span>
                    )}
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[o.status || "new"]}`}>
                      {o.status || "new"}
                    </span>
                    <span className="text-xs text-white/40">{formatAdminDate(o.created_at)}</span>
                  </div>
                  <div className="mt-1 text-sm text-white/80">{customer.displayName || ADMIN_PLACEHOLDER}{customer.phone ? ` / ${customer.phone}` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold">${(o.total || 0).toFixed(2)}</div>
                  <div className="text-[10px] text-white/40">{(o.items || []).length} {lang === "ar" ? "عنصر" : "item(s)"}</div>
                </div>
                {expanded === o.order_number ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
              </button>

              {expanded === o.order_number && (
                <div className="p-4 border-t border-white/5 bg-black/30">
                  <div className="grid md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">{t("dashboard.customer")}</div>
                      <div className="space-y-0.5">
                        <div><b>{customer.displayName || (lang === "ar" ? "لم يتم توفير العميل" : "Customer not provided")}</b></div>
                        {customer.customerId && <div className="text-[#F7A76F] font-mono">{customer.customerId}</div>}
                        <div className="text-white/60">{withPlaceholder(customer.phone)}</div>
                        <div className="text-white/60">{withPlaceholder(customer.email)}</div>
                        {customer.address && <div className="text-white/60">{customer.address}</div>}
                        <div className="text-white/60">
                          {[customer.city, customer.country].filter(Boolean).join(" / ") || ADMIN_PLACEHOLDER}
                        </div>
                        {customer.deliveryNotes && <div className="mt-1 text-white/50 italic">&ldquo;{customer.deliveryNotes}&rdquo;</div>}
                        <div className="text-white/45">{lang === "ar" ? "حالة البريد الإلكتروني" : "Email status"}: {verificationLabel(customer.verification)}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">{t("dashboard.paymentTotals")}</div>
                      <div className="space-y-0.5">
                        <div>Payment: <b className="uppercase">{o.payment_method}</b></div>
                        <div>Lang: <b className="uppercase">{o.language}</b></div>
                        <div>Subtotal: ${(o.subtotal || 0).toFixed(2)}</div>
                        <div>Shipping: ${(o.shipping_fee || 0).toFixed(2)}</div>
                        <div className="font-bold mt-1">Total: ${(o.total || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 text-white/40 uppercase tracking-wider text-[10px] mb-1.5">{t("dashboard.items")}</div>
                  <div className="space-y-1">
                    {(o.items || []).map((it, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2 text-xs bg-black/40 rounded-lg px-3 py-2">
                        <div className="min-w-0">
                          <span className="text-[#F37021] font-bold">#{it.product_id}</span> <span className="truncate">{it.name}</span>
                          {(it.size || it.color_display || it.color) && (
                            <span className="text-white/40"> - {[it.size, it.color_display || it.color].filter(Boolean).join(" / ")}</span>
                          )}
                        </div>
                        <div className="shrink-0">{it.quantity} x ${(it.unit_price || 0).toFixed(2)} = <b>${(it.line_total || 0).toFixed(2)}</b></div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-white/40">{t("dashboard.setStatus")}</span>
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => updateStatus(o.order_number, s)}
                        disabled={s === (o.status || "new")}
                        className={`h-8 px-3 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                          s === (o.status || "new")
                            ? "bg-white/10 text-white/40 border-white/10 cursor-not-allowed"
                            : "bg-white/5 hover:bg-[#F37021] border-white/10 hover:border-[#F37021] text-white"
                        }`}
                        data-testid={`order-status-${o.order_number}-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => softDeleteOrder(o.order_number)}
                      className="h-8 px-3 rounded-full text-[10px] font-bold uppercase tracking-wider border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 ms-auto"
                      data-testid={`order-soft-delete-${o.order_number}`}
                    >
                      <Trash2 className="w-3 h-3 inline -mt-0.5 me-1" />
                      Move to Trash
                    </button>
                  </div>

                  {o.cancellation_reason && (
                    <div className="mt-3 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[11px] text-red-200">
                      <b>Cancellation reason:</b> {o.cancellation_reason}
                    </div>
                  )}

                  <AdminNotesPanel orderNumber={o.order_number} initial={o.admin_notes || []} apiFetch={apiFetch} onAdded={reload} />
                </div>
              )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// STOCK
// ============================================================================
function StockTab() {
  const { apiFetch } = useAdminAuth();
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);
  const [overrides, setOverrides] = useState({});
  const [resyncing, setResyncing] = useState(false);
  const { data, loading } = useAdminFetch(apiFetch, "/admin/stock", [tick]);
  const reload = () => setTick((n) => n + 1);

  const resyncOldOos = async ({ confirm = true } = {}) => {
    if (confirm && !window.confirm("Set every product that was sold out in the old stock back to 0? In-stock counts stay as they are.")) return;
    setResyncing(true);
    try {
      const res = await apiFetch("/admin/stock/resync-oos?apply=true", { method: "POST" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || "Resync failed");
      const applied = body.applied ?? body.count ?? 0;
      if (applied > 0) {
        toast.success(`${applied} products set out of stock from old data`);
        setOverrides({});
        reload();
      } else if (confirm) {
        toast.info(body.skipped === "empty_source" ? "Old stock database is empty — nothing to copy" : "Already in sync with old sold-out rows");
      }
    } catch (err) {
      if (confirm) toast.error(err?.message || "Resync failed");
    } finally {
      setResyncing(false);
    }
  };

  useEffect(() => {
    resyncOldOos({ confirm: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch]);

  const productById = useMemo(() => {
    const m = {};
    ALL_PRODUCTS.forEach((p) => { m[p.id] = p; });
    return m;
  }, []);

  // Merge in-session overrides on top of fetched stock so user-edited
  // values persist visually until the next refresh.
  const stock = useMemo(() => {
    const base = data?.items || [];
    if (Object.keys(overrides).length === 0) return base;
    return base.map((s) => {
      const ov = overrides[s.product_id];
      if (!ov) return s;
      return { ...s, ...ov, sizes: { ...(s.sizes || {}), ...(ov.sizes || {}) } };
    });
  }, [data, overrides]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return stock;
    return stock.filter((s) => {
      const p = productById[s.product_id];
      const name = p ? (p.name?.en || "").toLowerCase() : "";
      return s.product_id.includes(needle) || name.includes(needle);
    });
  }, [q, stock, productById]);

  const updateVariant = async (productId, variantKey, qty) => {
    const res = await apiFetch(`/admin/stock/${productId}`, {
      method: "PUT",
      body: JSON.stringify({ variant_key: variantKey || null, qty: Math.max(0, Number(qty) || 0) }),
    });
    if (res.ok) {
      toast.success(`#${productId} ${variantKey || "stock"} -> ${qty}`);
      setOverrides((prev) => {
        const existing = prev[productId] || {};
        const next = { ...existing };
        if (variantKey) {
          next.sizes = { ...(existing.sizes || {}), [variantKey]: Number(qty) };
          next.has_sizes = true;
        } else {
          next.qty = Number(qty);
        }
        return { ...prev, [productId]: next };
      });
    } else {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-stock">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-2xl font-bold">Stock</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by #ID or name"
              className="h-9 ps-9 pe-3 rounded-full bg-white/5 border border-white/10 text-xs w-56"
              data-testid="stock-search"
            />
          </div>
          <button
            type="button"
            onClick={() => resyncOldOos({ confirm: true })}
            disabled={resyncing}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 text-xs disabled:opacity-50"
          >
            <RotateCcw className="w-3.5 h-3.5" /> {resyncing ? "Syncing…" : "Restore sold-out from old data"}
          </button>
          <button type="button" onClick={reload} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/5 hover:bg-white/10 text-xs">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : filtered.length === 0 ? (
        <Empty label="No stock records" />
      ) : (
        <div className="space-y-2">
          {filtered.map((s) => {
            const p = productById[s.product_id];
            const name = p ? localized(p.name, "en") : "-";
            const variants = s.has_sizes
              ? Object.entries(s.sizes || {})
              : [["", s.qty || 0]];
            return (
              <article key={s.product_id} className="bg-[#161616] rounded-2xl border border-white/5 p-4" data-testid={`stock-${s.product_id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <span className="text-[#F37021] font-mono font-bold text-xs">#{s.product_id}</span>
                    <span className="ms-2 text-sm">{name}</span>
                  </div>
                  <div className="text-[10px] text-white/40">
                    Total: {variants.reduce((acc, [, q]) => acc + (Number(q) || 0), 0)}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {variants.map(([vk, qty]) => (
                    <div key={vk || "__single"} className="flex items-center gap-2 bg-black/30 rounded-xl px-3 py-2">
                      <div className="flex-1 min-w-0 text-xs">
                        <span className="text-white/40 uppercase tracking-wider text-[10px]">{vk ? vk.replace("::", " / ") : "Single"}</span>
                      </div>
                      <StockInput
                        initial={Number(qty) || 0}
                        onSave={(v) => updateVariant(s.product_id, vk, v)}
                        testid={`stock-input-${s.product_id}-${(vk || "single").replace(/[^a-zA-Z0-9]/g, "-")}`}
                      />
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StockInput({ initial, onSave, testid }) {
  // Keying the input on `initial` is enough to reset its value when the
  // upstream stock changes ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â much cleaner than setState-in-effect.
  return (
    <StockInputInner key={initial} initial={initial} onSave={onSave} testid={testid} />
  );
}

function StockInputInner({ initial, onSave, testid }) {
  const [val, setVal] = useState(String(initial));
  const dirty = Number(val) !== Number(initial);
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={0}
        max={9999}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="w-16 h-7 px-2 rounded-lg bg-black/40 border border-white/10 text-xs text-center"
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => onSave(Number(val) || 0)}
        disabled={!dirty}
        className="h-7 px-2 rounded-lg bg-[#F37021] hover:bg-[#E06015] text-white text-[10px] font-bold disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={testid ? `${testid}-save` : undefined}
      >
        Save
      </button>
    </div>
  );
}

// ============================================================================
// PRODUCTS (full CRUD with overlay support)
// ============================================================================
function ProductsTab() {
  const { apiFetch } = useAdminAuth();
  const { version: catalogVersion, refresh: refreshCatalog } = useCatalog();
  const [q, setQ] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Catalog version bumps after every save, which re-derives `allProducts`.
  const allProducts = useMemo(() => ALL_PRODUCTS.slice(), [catalogVersion]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allProducts.filter((p) => {
      const hidden = isHiddenById(p.id);
      if (hidden && !showHidden) return false;
      if (!needle) return true;
      const n = (p.name?.en || "").toLowerCase();
      return p.id.toLowerCase().includes(needle) || n.includes(needle);
    });
  }, [q, allProducts, showHidden]);

  const onSaved = async () => {
    await refreshCatalog();
  };

  const onDelete = async (productId) => {
    if (!window.confirm(`Hide / delete product #${productId}?`)) return;
    const res = await apiFetch(`/admin/products/${productId}`, { method: "DELETE" });
    if (res.ok) {
      const data = await res.json();
      toast.success(data.mode === "deleted" ? "Deleted" : "Hidden");
      refreshCatalog();
    } else {
      toast.error("Delete failed");
    }
  };

  const onUnhide = async (productId) => {
    const res = await apiFetch(`/admin/products/${productId}`, { method: "PUT", body: JSON.stringify({ hidden: false }) });
    if (res.ok) {
      toast.success("Restored");
      refreshCatalog();
    } else {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-products">
      <div className="admin-panel px-4 py-4 sm:px-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold">Products <span className="text-sm text-white/40 font-normal">({filtered.length})</span></h2>
          <p className="text-xs text-white/40 mt-0.5">Add, edit, hide, or restore products. Changes appear live on the public site after save.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-white/60 cursor-pointer">
            <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} data-testid="products-show-hidden" />
            Show hidden
          </label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by #ID or name"
              className="admin-input h-9 ps-9 pe-3 text-xs w-56"
              data-testid="products-search"
            />
          </div>
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-bold"
            data-testid="products-bulk"
          >
            <Upload className="w-3.5 h-3.5" /> Bulk CSV
          </button>
          <button
            type="button"
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#F37021] hover:bg-[#E06015] text-xs font-bold text-white"
            data-testid="products-new"
          >
            <Plus className="w-3.5 h-3.5" /> New product
          </button>
        </div>
      </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.slice(0, 300).map((p) => {
          const pct = getProductDiscountPct(p);
          const disc = computeDiscountedPrice(p.originalPrice, pct);
          const hidden = isHiddenById(p.id);
          return (
            <article key={p.id} className={`admin-panel-soft ${hidden ? "border-red-500/30 opacity-70" : ""} p-3 flex gap-3`} data-testid={`product-${p.id}`}>
              <div className="shrink-0 w-16 h-20 rounded-xl overflow-hidden bg-[#0F0F0F]">
                {p.images?.[0] && <StoreImg src={p.images[0]} alt="" width={160} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[#F37021] font-mono text-[10px] font-bold">#{p.id}</span>
                  <span className="text-[10px] uppercase tracking-wider text-white/40">{p.category}</span>
                  {hidden && <span className="text-[10px] uppercase tracking-wider text-red-400 font-bold">Hidden</span>}
                </div>
                <div className="text-xs font-semibold line-clamp-2 mt-0.5">{localized(p.name, "en")}</div>
                {p.originalPrice != null && (
                  <div className="mt-1 text-xs">
                    <span className="text-[#F37021] font-bold">{formatPrice(disc)}</span>
                    <span className="ms-1.5 text-white/30 line-through">{formatPrice(p.originalPrice)}</span>
                    <span className="ms-1.5 text-emerald-400 text-[10px] font-bold">-{pct}%</span>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => { setEditing(p); setFormOpen(true); }}
                    className="h-7 px-3 rounded-full bg-white/5 hover:bg-white/10 text-[10px] font-bold uppercase tracking-wider"
                    data-testid={`product-edit-${p.id}`}
                  >Edit</button>
                  {hidden ? (
                    <button type="button" onClick={() => onUnhide(p.id)} className="h-7 px-3 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider" data-testid={`product-restore-${p.id}`}>Restore</button>
                  ) : (
                    <button type="button" onClick={() => onDelete(p.id)} className="h-7 px-3 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 text-[10px] font-bold uppercase tracking-wider" data-testid={`product-delete-${p.id}`}>Hide</button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
      {filtered.length > 300 && (
        <p className="mt-4 text-center text-xs text-white/40">Showing first 300 of {filtered.length} - refine search to narrow.</p>
      )}

      <AdminProductForm
        key={editing?.id || "new"}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        existing={editing}
        apiFetch={apiFetch}
        onSaved={onSaved}
      />
      <BulkUploadModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        apiFetch={apiFetch}
        onImported={onSaved}
      />
    </div>
  );
}

// ============================================================================
// ANALYTICS
// ============================================================================
function AnalyticsTab() {
  const { apiFetch } = useAdminAuth();
  const { t, lang } = useAdminI18n();
  const [range, setRange] = useState("30");
  const { data, loading, error } = useAdminFetch(apiFetch, `/admin/analytics?range_days=${range}`, [range]);
  const a = (key, fallback) => t(`analytics.${key}`, fallback);

  if (loading) return <Loading />;
  if (error) return <ErrorState label={error} />;
  if (!data) return <Empty label={a("notAvailable", "No analytics available")} />;

  const cards = [
    { label: a("today", "Today"), value: data.today, accent: "text-[#F37021]" },
    { label: a("last7Days", "Last 7 days"), value: data.last_7_days, accent: "text-blue-400" },
    { label: a("last30Days", "Last 30 days"), value: data.last_30_days, accent: "text-emerald-400" },
    { label: a("allTime", "All time"), value: data.all_time, accent: "text-white" },
  ];
  const customerStats = data.customers || {};
  const productStats = data.products || {};
  const shippingStats = data.shipping || {};
  const tracking = data.tracking || {};
  const logs = data.logs || {};
  const overview = data.overview || {};
  const traffic = data.traffic || {};
  const productInterest = data.product_interest || {};
  const funnel = data.funnel || {};
  const whatsapp = data.whatsapp || {};
  const shippingAnalytics = shippingStats.analytics || {};
  const chinaSuppliers = data.china_suppliers || {};
  const authEvents = data.auth_events || {};
  const sourceStats = data.analytics_sources || {};
  const sourceBreakdowns = data.source_breakdowns || {};
  const rangeLabel = range === "all" ? a("allTime", "All time") : a("lastDays", "Last {days} days").replace("{days}", range);
  const appNote = sourceStats.note ? a("appNotConnected", "App analytics are not connected yet.") : "";
  const sourceRows = [
    { key: "website", label: a("website", "Website"), stats: sourceStats.website || {}, accent: "text-[#F58D4A]" },
    { key: "app", label: a("mobileApp", "Mobile app"), stats: sourceStats.app || {}, accent: "text-emerald-300" },
    { key: "unknown", label: a("unknown", "Unknown"), stats: sourceStats.unknown || {}, accent: "text-white/75" },
    { key: "total", label: a("total", "Total"), stats: sourceStats.combined || {}, accent: "text-white" },
  ];
  const renderList = (items, emptyLabel, renderItem) => {
    if (!Array.isArray(items) || items.length === 0) return <Empty label={emptyLabel} small />;
    return <div className="space-y-2">{items.map(renderItem)}</div>;
  };
  const sourceLabel = (value) => {
    if (value === "website") return a("website", "Website");
    if (value === "app" || value === "mobile_app") return a("mobileApp", "Mobile app");
    if (value === "unknown") return a("unknown", "Unknown");
    return value || ADMIN_PLACEHOLDER;
  };
  const normalizedSources = (counts, fallbackTotal = 0) => {
    if (!counts || typeof counts !== "object") {
      const unknown = Number(fallbackTotal) || 0;
      return { website: 0, mobile_app: 0, unknown, total: unknown };
    }
    const website = Number(counts.website) || 0;
    const mobile_app = Number(counts.mobile_app ?? counts.app) || 0;
    const unknown = Number(counts.unknown) || 0;
    return { website, mobile_app, unknown, total: website + mobile_app + unknown };
  };
  const SourceSplit = ({ counts, fallbackTotal = 0 }) => {
    const values = normalizedSources(counts, fallbackTotal);
    return (
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-white/6 pt-2 text-[10px] text-white/48 sm:grid-cols-4">
        <span>{a("website", "Website")}: <b className="text-white/78">{values.website}</b></span>
        <span>{a("mobileApp", "Mobile app")}: <b className="text-white/78">{values.mobile_app}</b></span>
        <span>{a("unknown", "Unknown")}: <b className="text-white/78">{values.unknown}</b></span>
        <span>{a("total", "Total")}: <b className="text-white">{values.total}</b></span>
      </div>
    );
  };
  const MetricRow = ({ label, value, counts }) => (
    <div className="border-b border-white/6 py-2.5 last:border-b-0">
      <div className="flex items-center justify-between gap-3 text-xs"><span>{label}</span><b>{value ?? 0}</b></div>
      <SourceSplit counts={counts} fallbackTotal={value ?? 0} />
    </div>
  );
  const describeEvent = (event) => {
    const bits = [];
    if (event?.product?.id) bits.push(`#${event.product.id}${event.product.title ? ` ${event.product.title}` : ""}`);
    if (event?.country?.name || event?.country?.code) bits.push(event.country?.name || event.country?.code);
    if (event?.shipping?.method) bits.push(event.shipping.method);
    if (event?.shipping?.category) bits.push(event.shipping.category);
    if (event?.metadata?.source) bits.push(event.metadata.source);
    if (event?.metadata?.reason) bits.push(event.metadata.reason);
    return bits.join(" • ") || ADMIN_PLACEHOLDER;
  };

  return (
    <div data-testid="admin-analytics">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold">{a("title", "Analytics")}</h2>
          <p className="mt-1 text-xs text-white/45">{a("description", "Business metrics remain intact. Event metrics below separate website, app, unknown, and clearly labeled combined totals.")}</p>
        </div>
        <div className="inline-flex flex-wrap gap-2">
          {["7", "30", "90", "all"].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setRange(option)}
              className={`h-9 rounded-full border px-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                range === option
                  ? "border-[#F37021] bg-[#F37021] text-white"
                  : "border-white/10 bg-white/5 text-white/65 hover:bg-white/10"
              }`}
            >
              {option === "all" ? a("all", "All") : a("daysShort", "{days}d").replace("{days}", option)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <article key={c.label} className="bg-[#161616] rounded-2xl border border-white/5 p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/40">{c.label}</div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <div>
                <div className={`text-2xl font-bold ${c.accent}`}>{c.value?.count ?? 0}</div>
                <div className="text-[11px] text-white/40">{a("orders", "orders")}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold">${(c.value?.revenue ?? 0).toFixed(2)}</div>
                <div className="text-[11px] text-white/40">{a("revenue", "revenue")}</div>
                <div className="text-[11px] text-white/30">{a("avg", "avg")} ${(c.value?.avg_order_value ?? 0).toFixed(2)}</div>
              </div>
            </div>
            <SourceSplit
              counts={c.value?.sources && {
                website: c.value.sources.website?.count,
                mobile_app: c.value.sources.mobile_app?.count,
                unknown: c.value.sources.unknown?.count,
              }}
              fallbackTotal={c.value?.count ?? 0}
            />
          </article>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3 mt-3">
        {[
          { label: a("pageViews", "Page views"), value: overview.page_views ?? 0, hint: rangeLabel, accent: "text-blue-300", counts: sourceBreakdowns.overview?.page_views },
          { label: a("uniqueSessions", "Unique sessions"), value: overview.unique_sessions ?? 0, hint: rangeLabel, accent: "text-cyan-300", counts: sourceBreakdowns.overview?.unique_sessions },
          { label: a("whatsappClicks", "WhatsApp clicks"), value: overview.whatsapp_clicks ?? 0, hint: rangeLabel, accent: "text-emerald-300", counts: sourceBreakdowns.overview?.whatsapp_clicks },
          { label: a("searches", "Searches"), value: productInterest.search_count ?? 0, hint: rangeLabel, accent: "text-amber-300", counts: sourceBreakdowns.product_interest?.search_count },
          { label: a("addToCart", "Add to cart"), value: productInterest.add_to_cart_success ?? 0, hint: a("failedCount", "{count} failed").replace("{count}", productInterest.add_to_cart_failed ?? 0), accent: "text-[#F58D4A]", counts: sourceBreakdowns.product_interest?.add_to_cart_success },
          { label: a("checkoutStarts", "Checkout starts"), value: funnel.checkout_starts ?? 0, hint: a("successCount", "{count} success").replace("{count}", funnel.checkout_success ?? 0), accent: "text-violet-300", counts: sourceBreakdowns.funnel?.checkout_starts },
        ].map((card) => (
          <article key={card.label} className="bg-[#161616] rounded-2xl border border-white/5 p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/40">{card.label}</div>
            <div className={`mt-3 text-2xl font-bold ${card.accent}`}>{card.value}</div>
            <div className="mt-1 text-[11px] text-white/40">{card.hint}</div>
            <SourceSplit counts={card.counts} fallbackTotal={card.value} />
          </article>
        ))}
      </div>

      <article className="bg-[#161616] rounded-2xl border border-white/5 p-4 mt-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold">{a("sourceTitle", "Website / app source split")}</h3>
            <p className="mt-1 text-xs text-white/45">{a("sourceDescription", "Combined totals are shown only in the combined column; app rows stay zero until app-source events are connected.")}</p>
          </div>
          {appNote && (
            <div className="rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1.5 text-[11px] font-semibold text-amber-200">
              {appNote}
            </div>
          )}
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {sourceRows.map((row) => (
            <div key={row.key} className="rounded-xl border border-white/6 bg-black/20 p-3">
              <div className={`text-sm font-bold ${row.accent}`}>{row.label}</div>
              <div className="mt-3 space-y-1.5 text-xs text-white/72">
                <div className="flex items-center justify-between"><span>{a("visits", "Visits")}</span><b>{row.stats.visits ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("events", "Events")}</span><b>{row.stats.events ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("productViews", "Product views")}</span><b>{row.stats.product_views ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("cartEvents", "Cart events")}</span><b>{row.stats.cart_events ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("checkoutEvents", "Checkout events")}</span><b>{row.stats.checkout_events ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("whatsappClicks", "WhatsApp clicks")}</span><b>{row.stats.whatsapp_clicks ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("warehouseViews", "Warehouse views")}</span><b>{row.stats.warehouse_views ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("addressCopies", "Address copies")}</span><b>{row.stats.address_copies ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("shippingMarkCopies", "Shipping mark copies")}</span><b>{row.stats.shipping_mark_copies ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("shippingCalculatorViews", "Shipping calculator views")}</span><b>{row.stats.shipping_calculator_views ?? 0}</b></div>
                <div className="flex items-center justify-between"><span>{a("authEvents", "Login / signup events")}</span><b>{row.stats.auth_events ?? 0}</b></div>
                {row.key === "app" && row.stats.external_events > 0 && (
                  <div className="flex items-center justify-between text-white/45"><span>{a("externalEvents", "External app rows")}</span><b>{row.stats.external_events}</b></div>
                )}
              </div>
            </div>
          ))}
        </div>
      </article>

      <div className="grid lg:grid-cols-2 2xl:grid-cols-4 gap-3 mt-3">
        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <h3 className="text-sm font-bold mb-3">{a("customers", "Customers")}</h3>
          <div className="space-y-2 text-white/75">
            <MetricRow label={a("registered", "Registered")} value={customerStats.registered_total} counts={sourceBreakdowns.customers?.registered_total} />
            <MetricRow label={a("verified", "Verified")} value={customerStats.verified_total} counts={sourceBreakdowns.customers?.verified_total} />
            <MetricRow label={a("unverified", "Unverified")} value={customerStats.unverified_total} counts={sourceBreakdowns.customers?.unverified_total} />
            <MetricRow label={a("withOrders", "With orders")} value={customerStats.customers_with_orders} counts={sourceBreakdowns.customers?.customers_with_orders} />
            <MetricRow label={a("newIn30Days", "New in 30 days")} value={customerStats.new_last_30_days} counts={sourceBreakdowns.customers?.new_last_30_days} />
            <MetricRow label={a("missingProfileData", "Missing profile data")} value={customerStats.missing_profile_data} counts={sourceBreakdowns.customers?.missing_profile_data} />
          </div>
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <h3 className="text-sm font-bold mb-3">{a("products", "Products")}</h3>
          <div className="space-y-2 text-white/75">
            <MetricRow label={a("staticCatalog", "Static catalog")} value={productStats.static_catalog_products} counts={sourceBreakdowns.products?.static_catalog_products} />
            <MetricRow label={a("dbProducts", "DB products")} value={productStats.db_products} counts={sourceBreakdowns.products?.db_products} />
            <MetricRow label={a("overrideRows", "Override rows")} value={productStats.override_rows} counts={sourceBreakdowns.products?.override_rows} />
            <MetricRow label={a("hidden", "Hidden")} value={productStats.hidden_products} counts={sourceBreakdowns.products?.hidden_products} />
            <MetricRow label={a("outOfStock", "Out of stock")} value={productStats.out_of_stock_products} counts={sourceBreakdowns.products?.out_of_stock_products} />
            <MetricRow label={a("orderRowsMissingIdentity", "Order rows missing identity")} value={productStats.missing_order_item_identity_rows} counts={sourceBreakdowns.products?.missing_order_item_identity_rows} />
          </div>
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <h3 className="text-sm font-bold mb-3">{a("shipping", "Shipping")}</h3>
          <div className="space-y-2 text-white/75">
            <MetricRow label={a("rates", "Rates")} value={shippingStats.shipping_rates_count} counts={sourceBreakdowns.shipping?.shipping_rates_count} />
            <MetricRow label={a("enabledRates", "Enabled rates")} value={shippingStats.enabled_shipping_rates} counts={sourceBreakdowns.shipping?.enabled_shipping_rates} />
            <MetricRow label={a("rateCountries", "Rate countries")} value={shippingStats.shipping_rate_countries} counts={sourceBreakdowns.shipping?.shipping_rate_countries} />
            <MetricRow label={a("rateMethods", "Rate methods")} value={shippingStats.shipping_rate_methods} counts={sourceBreakdowns.shipping?.shipping_rate_methods} />
            <MetricRow label={a("shippingOnlyEntries", "Shipping Only entries")} value={shippingStats.shipping_only_entries_count} counts={sourceBreakdowns.shipping?.shipping_only_entries_count} />
            <MetricRow label={a("activeWarehouses", "Active warehouses")} value={shippingStats.warehouses_active} counts={sourceBreakdowns.shipping?.warehouses_active} />
            <MetricRow label={a("warehouses", "Warehouses")} value={shippingStats.warehouses_total} counts={sourceBreakdowns.shipping?.warehouses_total} />
          </div>
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <h3 className="text-sm font-bold mb-3">{a("trackingHealth", "Tracking health")}</h3>
          <div className="rounded-xl border border-dashed border-white/10 bg-black/20 px-3 py-3 text-xs text-white/65">
            <div className="font-semibold text-white/80">{tracking.message || a("trackingNotAvailable", "Tracking data not available yet.")}</div>
            <div className="mt-2 space-y-1 text-white/50">
              <div>{a("rangeEvents", "Range events")}: <span className="text-white/75">{tracking.total_events ?? 0}</span></div>
              <div>{a("allTimeEvents", "All-time events")}: <span className="text-white/75">{tracking.all_time_events ?? 0}</span></div>
              <div>{a("lastEvent", "Last event")}: <span className="text-white/75">{tracking.last_event_at ? formatAdminDate(tracking.last_event_at) : ADMIN_PLACEHOLDER}</span></div>
              <div>{a("range", "Range")}: <span className="text-white/75">{rangeLabel}</span></div>
            </div>
            <SourceSplit counts={sourceBreakdowns.tracking?.range_events} fallbackTotal={tracking.total_events ?? 0} />
            {Array.isArray(tracking.unavailable_metrics) && tracking.unavailable_metrics.length > 0 && (
              <div className="mt-3 space-y-2">
                {tracking.unavailable_metrics.map((item) => (
                  <div key={item.metric} className="rounded-lg border border-white/6 bg-white/[0.03] px-2.5 py-2">
                    <div className="font-semibold text-white/80">{friendlyEventName(item.metric, lang)}</div>
                    <div className="mt-1 text-[11px] leading-5 text-white/45">{item.reason}</div>
                  </div>
                ))}
              </div>
            )}
            {Array.isArray(tracking.collections_found) && tracking.collections_found.length > 0 && (
              <div className="mt-2 text-white/45">{a("collectionsFound", "Collections found")}: {tracking.collections_found.join(", ")}</div>
            )}
          </div>
        </article>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mt-3">
        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold">{a("traffic", "Traffic")}</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">{rangeLabel}</span>
          </div>
          {renderList(traffic.top_pages, a("noPageViews", "No page views yet"), (item) => (
            <div key={item.key} className="rounded-xl border border-white/6 bg-black/20 px-3 py-2.5 text-xs">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-white/85">{friendlyPathName(item.key, lang)}</span>
                <span className="font-bold text-white">{item.count}</span>
              </div>
              <SourceSplit counts={item.source_counts} fallbackTotal={item.count} />
            </div>
          ))}
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/6 bg-black/20 p-3">
              <div className="mb-2 text-[10px] uppercase tracking-wider text-white/40">{a("languages", "Languages")}</div>
              {renderList(traffic.views_by_language, a("noLanguageData", "No language data"), (item) => (
                <div key={item.key} className="py-1 text-xs">
                  <div className="flex items-center justify-between"><span>{item.key || ADMIN_PLACEHOLDER}</span><span className="font-bold">{item.count}</span></div>
                  <SourceSplit counts={item.source_counts} fallbackTotal={item.count} />
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-white/6 bg-black/20 p-3">
              <div className="mb-2 text-[10px] uppercase tracking-wider text-white/40">{a("devices", "Devices")}</div>
              {renderList(traffic.device_mix, a("noDeviceData", "No device data"), (item) => (
                <div key={item.key} className="py-1 text-xs">
                  <div className="flex items-center justify-between"><span>{item.key || ADMIN_PLACEHOLDER}</span><span className="font-bold">{item.count}</span></div>
                  <SourceSplit counts={item.source_counts} fallbackTotal={item.count} />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-white/6 bg-black/20 p-3">
            <div className="mb-2 text-[10px] uppercase tracking-wider text-white/40">{a("countryPages", "Country pages")}</div>
            {renderList(traffic.country_page_views, a("noCountryPageViews", "No country page views yet"), (item) => (
              <div key={item.key} className="py-1 text-xs">
                <div className="flex items-center justify-between"><span>{item.label || item.key}</span><span className="font-bold">{item.count}</span></div>
                <SourceSplit counts={item.source_counts} fallbackTotal={item.count} />
              </div>
            ))}
          </div>
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold">{a("productInterest", "Product interest")}</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">{rangeLabel}</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { label: a("viewed", "Viewed"), value: funnel.product_views ?? 0, tone: "text-blue-300", counts: sourceBreakdowns.funnel?.product_views },
              { label: a("addToCart", "Add to cart"), value: productInterest.add_to_cart_success ?? 0, tone: "text-emerald-300", counts: sourceBreakdowns.product_interest?.add_to_cart_success },
              { label: a("failedAdds", "Failed adds"), value: productInterest.add_to_cart_failed ?? 0, tone: "text-red-300", counts: sourceBreakdowns.product_interest?.add_to_cart_failed },
            ].map((item) => (
              <div key={item.label} className="rounded-xl border border-white/6 bg-black/20 p-3">
                <div className="text-[10px] uppercase tracking-wider text-white/40">{item.label}</div>
                <div className={`mt-2 text-2xl font-bold ${item.tone}`}>{item.value}</div>
                <SourceSplit counts={item.counts} fallbackTotal={item.value} />
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-white/6 bg-black/20 p-3">
            <div className="mb-2 text-[10px] uppercase tracking-wider text-white/40">{a("topViewedProducts", "Top viewed products")}</div>
            {renderList(productInterest.top_viewed_products, a("noProductViews", "No product views yet"), (item, index) => (
              <div key={item.key} className="py-1.5 text-xs">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-white/35 font-mono me-2">{index + 1}.</span>
                    <span className="font-semibold text-white/85">{item.label || item.key}</span>
                  </div>
                  <span className="font-bold">{item.count}</span>
                </div>
                <SourceSplit counts={item.source_counts} fallbackTotal={item.count} />
              </div>
            ))}
          </div>
        </article>
      </div>

      <div className="grid lg:grid-cols-3 gap-3 mt-3">
        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold">{a("funnel", "Funnel")}</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">{rangeLabel}</span>
          </div>
          <div className="space-y-2">
            {[
              ["product_views", a("productViews", "Product views"), funnel.product_views],
              ["add_to_cart", a("addToCart", "Add to cart"), funnel.add_to_cart],
              ["cart_views", a("cartViews", "Cart views"), funnel.cart_views],
              ["checkout_starts", a("checkoutStarts", "Checkout starts"), funnel.checkout_starts],
              ["checkout_success", a("checkoutSuccess", "Checkout success"), funnel.checkout_success],
              ["checkout_failed", a("checkoutFailed", "Checkout failed"), funnel.checkout_failed],
            ].map(([key, label, value]) => (
              <div key={key} className="rounded-xl border border-white/6 bg-black/20 px-3 py-2.5 text-xs">
                <div className="flex items-center justify-between"><span className="text-white/75">{label}</span><span className="font-bold text-white">{value ?? 0}</span></div>
                <SourceSplit counts={sourceBreakdowns.funnel?.[key]} fallbackTotal={value ?? 0} />
              </div>
            ))}
          </div>
          {Array.isArray(funnel.missing_steps) && funnel.missing_steps.length > 0 && (
            <div className="mt-3 text-[11px] leading-5 text-white/45">
              {a("missingStepData", "Missing step data")}: {funnel.missing_steps.map((eventName) => friendlyEventName(eventName, lang)).join(", ")}
            </div>
          )}
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold">WhatsApp</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">{rangeLabel}</span>
          </div>
          <div className="rounded-xl border border-white/6 bg-black/20 p-3">
            <div className="text-[10px] uppercase tracking-wider text-white/40">{a("totalClicks", "Total clicks")}</div>
            <div className="mt-2 text-2xl font-bold text-emerald-300">{whatsapp.total_clicks ?? 0}</div>
            <SourceSplit counts={sourceBreakdowns.whatsapp?.total_clicks} fallbackTotal={whatsapp.total_clicks ?? 0} />
          </div>
          <div className="mt-4 rounded-xl border border-white/6 bg-black/20 p-3">
            <div className="mb-2 text-[10px] uppercase tracking-wider text-white/40">{a("topPages", "Top pages")}</div>
            {renderList(whatsapp.by_page, a("noWhatsappClicks", "No WhatsApp clicks yet"), (item) => (
              <div key={item.key} className="py-1 text-xs">
                <div className="flex items-center justify-between"><span className="truncate">{friendlyPathName(item.key, lang)}</span><span className="font-bold">{item.count}</span></div>
                <SourceSplit counts={item.source_counts} fallbackTotal={item.count} />
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-white/6 bg-black/20 p-3">
            <div className="mb-2 text-[10px] uppercase tracking-wider text-white/40">{a("byCountry", "By country")}</div>
            {renderList(whatsapp.by_country, a("noCountryMetadata", "No country metadata yet"), (item) => (
              <div key={item.key} className="py-1 text-xs">
                <div className="flex items-center justify-between"><span>{item.label || item.key}</span><span className="font-bold">{item.count}</span></div>
                <SourceSplit counts={item.source_counts} fallbackTotal={item.count} />
              </div>
            ))}
          </div>
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold">{a("shippingTools", "Shipping tools")}</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">{rangeLabel}</span>
          </div>
          <div className="space-y-2 text-white/75">
            <MetricRow label={a("calculatorViews", "Calculator views")} value={shippingAnalytics.calculator_views} counts={sourceBreakdowns.shipping_tools?.calculator_views} />
            <MetricRow label={a("calculations", "Calculations")} value={shippingAnalytics.calculations} counts={sourceBreakdowns.shipping_tools?.calculations} />
            <MetricRow label={a("countrySelections", "Country selections")} value={shippingAnalytics.country_selections} counts={sourceBreakdowns.shipping_tools?.country_selections} />
            <MetricRow label={a("methodSelections", "Method selections")} value={shippingAnalytics.method_selections} counts={sourceBreakdowns.shipping_tools?.method_selections} />
            <MetricRow label={a("shippingOnlyOpens", "Shipping Only opens")} value={shippingAnalytics.shipping_only_opens} counts={sourceBreakdowns.shipping_tools?.shipping_only_opens} />
            <MetricRow label={a("addressCopies", "Address copies")} value={shippingAnalytics.address_copies} counts={sourceBreakdowns.shipping_tools?.address_copies} />
            <MetricRow label={a("shippingMarkCopies", "Shipping mark copies")} value={shippingAnalytics.shipping_mark_copies} counts={sourceBreakdowns.shipping_tools?.shipping_mark_copies} />
          </div>
          <div className="mt-4 rounded-xl border border-white/6 bg-black/20 p-3">
            <div className="mb-2 text-[10px] uppercase tracking-wider text-white/40">{a("topMethods", "Top methods")}</div>
            {renderList(shippingAnalytics.top_methods, a("noMethodSelections", "No method selections yet"), (item) => (
              <div key={item.key} className="py-1 text-xs">
                <div className="flex items-center justify-between"><span>{item.key}</span><span className="font-bold">{item.count}</span></div>
                <SourceSplit counts={item.source_counts} fallbackTotal={item.count} />
              </div>
            ))}
          </div>
        </article>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mt-3">
        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold">{a("chinaSuppliersAuth", "China suppliers + auth")}</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">{rangeLabel}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/6 bg-black/20 p-3 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-white/40">{a("chinaSuppliers", "China suppliers")}</div>
              <div className="mt-2 space-y-2">
                <MetricRow label={a("sectionViews", "Section views")} value={chinaSuppliers.section_views} counts={sourceBreakdowns.china_suppliers?.section_views} />
                <MetricRow label={a("openClicks", "Open clicks")} value={chinaSuppliers.open_clicks} counts={sourceBreakdowns.china_suppliers?.open_clicks} />
              </div>
            </div>
            <div className="rounded-xl border border-white/6 bg-black/20 p-3 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-white/40">{a("customerAuth", "Customer auth")}</div>
              <div className="mt-2 space-y-2">
                <MetricRow label={a("loginSuccess", "Login success")} value={authEvents.login_success} counts={sourceBreakdowns.auth_events?.login_success} />
                <MetricRow label={a("loginFailed", "Login failed")} value={authEvents.login_failed} counts={sourceBreakdowns.auth_events?.login_failed} />
                <MetricRow label={a("signupSuccess", "Signup success")} value={authEvents.signup_success} counts={sourceBreakdowns.auth_events?.signup_success} />
                <MetricRow label={a("signupFailed", "Signup failed")} value={authEvents.signup_failed} counts={sourceBreakdowns.auth_events?.signup_failed} />
                <MetricRow label={a("verificationSent", "Verification sent")} value={authEvents.verification_requested} counts={sourceBreakdowns.auth_events?.verification_requested} />
                <MetricRow label={a("verificationSuccess", "Verification success")} value={authEvents.verification_success} counts={sourceBreakdowns.auth_events?.verification_success} />
                <MetricRow label={a("passwordResetRequests", "Password reset requests")} value={authEvents.password_reset_requested} counts={sourceBreakdowns.auth_events?.password_reset_requested} />
              </div>
            </div>
          </div>
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-bold">{a("recentEvents", "Recent events")}</h3>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/35">{rangeLabel}</span>
          </div>
          {renderList(traffic.recent_events, a("noTrackedEvents", "No tracked events yet"), (event, index) => (
            <div key={`${event.event_type}-${event.created_at || index}`} className="rounded-xl border border-white/6 bg-black/20 px-3 py-2.5 text-xs">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-white/85">{friendlyEventName(event.event_type, lang)}</div>
                  <div className="mt-1 inline-flex rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/60">
                    {a("source", "Source")}: {sourceLabel(event.source)}
                  </div>
                  <div className="mt-1 truncate text-white/45">{friendlyPathName(event.page_path, lang)}</div>
                  <div className="mt-1 text-white/35">{describeEvent(event)}</div>
                </div>
                <div className="shrink-0 text-right text-white/40">
                  <div>{formatAdminDate(event.created_at)}</div>
                  <div className="mt-1 uppercase tracking-wider text-[10px]">{event.language || ADMIN_PLACEHOLDER}</div>
                </div>
              </div>
            </div>
          ))}
        </article>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mt-3">
        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <h3 className="text-sm font-bold mb-3">{a("byStatusLifetime", "By status (lifetime)")}</h3>
          <div className="space-y-1.5">
            {Object.entries(data.by_status || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[k] || STATUS_STYLES.new}`}>{friendlyStatus(k, lang)}</span>
                <span className="font-bold">{v}</span>
              </div>
            ))}
            {Object.keys(data.by_status || {}).length === 0 && <Empty label={a("noOrders", "No orders")} small />}
          </div>
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <h3 className="text-sm font-bold mb-3">{a("topProducts30d", "Top products (30d)")}</h3>
          {(data.top_products || []).length === 0 ? (
            <Empty label={a("noDataYet", "No data yet")} small />
          ) : (
            <ol className="space-y-1.5">
              {(data.top_products || []).map((p, i) => (
                <li key={p.product_id || `${p.name}-${i}`} className="flex items-center justify-between text-xs gap-2">
                  <div className="min-w-0">
                    <span className="text-white/30 font-mono me-2">{i + 1}.</span>
                    <span className="text-[#F37021] font-mono font-bold me-1">#{p.product_id}</span>
                    <span className="truncate">{p.name || ADMIN_PLACEHOLDER}</span>
                  </div>
                  <div className="shrink-0 text-white/60">
                    {p.qty} {a("units", "units")} • <b>${(p.revenue ?? 0).toFixed(2)}</b>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </article>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mt-3">
        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <h3 className="text-sm font-bold mb-3">{a("topCustomers", "Top customers")}</h3>
          {(customerStats.top_customers || []).length === 0 ? (
            <Empty label={a("noCustomerOrderData", "No customer order data yet")} small />
          ) : (
            <div className="space-y-2">
              {customerStats.top_customers.map((customer, index) => {
                const identity = extractCustomerIdentity(customer);
                return (
                  <div key={`${identity.customerId || identity.email || identity.phone || index}`} className="rounded-xl border border-white/6 bg-black/20 px-3 py-2.5 text-xs">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white/35 font-mono">{index + 1}.</span>
                          <span className="font-semibold text-white/90">{identity.displayName || ADMIN_PLACEHOLDER}</span>
                          {identity.customerId && <span className="rounded-full border border-[#F37021]/25 bg-[#F37021]/10 px-2 py-0.5 font-mono text-[10px] text-[#F58D4A]">{identity.customerId}</span>}
                        </div>
                        <div className="mt-1 text-white/50">{identity.email || identity.phone || ADMIN_PLACEHOLDER}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-bold text-white">{customer.order_count || 0} {a("orders", "orders")}</div>
                        <div className="text-white/45">${(customer.total_order_value ?? 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </article>

        <article className="bg-[#161616] rounded-2xl border border-white/5 p-4">
          <h3 className="text-sm font-bold mb-3">{a("recentOrders", "Recent orders")}</h3>
          {(data.recent_orders || []).length === 0 ? (
            <Empty label={a("noRecentOrders", "No recent orders")} small />
          ) : (
            <div className="space-y-2">
              {data.recent_orders.map((order) => {
                const identity = extractCustomerIdentity(order.customer);
                return (
                  <div key={order.order_number} className="rounded-xl border border-white/6 bg-black/20 px-3 py-2.5 text-xs">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[#F37021] font-bold">{order.order_number}</span>
                          <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[order.status || "new"] || STATUS_STYLES.new}`}>{friendlyStatus(order.status || "new", lang)}</span>
                        </div>
                        <div className="mt-1 text-white/70">{identity.displayName || identity.email || identity.phone || ADMIN_PLACEHOLDER}</div>
                        <div className="mt-1 text-white/40">{formatAdminDate(order.created_at)}</div>
                      </div>
                      <div className="shrink-0 text-right font-semibold text-white">${(order.total ?? 0).toFixed(2)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </article>
      </div>

      <article className="bg-[#161616] rounded-2xl border border-white/5 p-4 mt-3">
        <h3 className="text-sm font-bold mb-3">{a("recentAdminLogs", "Recent admin logs")}</h3>
        {(logs.recent || []).length === 0 ? (
          <Empty label={a("noAdminLogs", "No admin logs yet")} small />
        ) : (
          <div className="space-y-2">
            {(logs.recent || []).map((log) => (
              <div key={log.id} className="rounded-xl border border-white/6 bg-black/20 px-3 py-2.5 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-white/90">{friendlyActionName(log.action || "admin.action", lang)}</div>
                    <div className="mt-1 text-white/45">{log.actor || ADMIN_PLACEHOLDER} • {log.item_kind || ADMIN_PLACEHOLDER} • {log.item_id || ADMIN_PLACEHOLDER}</div>
                  </div>
                  <div className="shrink-0 text-white/40">{formatAdminDate(log.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 text-[11px] text-white/35">{a("totalLoggedActions", "Total logged admin actions")}: {logs.total ?? 0}</div>
      </article>
    </div>
  );
}

// ============================================================================
// ADMIN NOTES PANEL (per-order, admin-only ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â never shown to customer)
// ============================================================================
function AdminNotesPanel({ orderNumber, initial, apiFetch, onAdded }) {
  const [notes, setNotes] = useState(Array.isArray(initial) ? initial : []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const addNote = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/admin/orders/${orderNumber}/notes`, {
        method: "POST",
        body: JSON.stringify({ text: t }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setNotes((prev) => [...prev, data.note]);
      setText("");
      toast.success("Note added");
      // Tell the parent to refetch /admin/orders so the new note is part
      // of the cached payload ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â survives collapse/expand without a manual
      // Refresh click.
      if (typeof onAdded === "function") onAdded();
    } catch (err) {
      toast.error(err?.message || "Could not add note");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid={`admin-notes-${orderNumber}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-amber-300 flex items-center gap-1.5">
          <FileText className="w-3 h-3" /> Admin notes ({notes.length}) - private
        </span>
      </div>
      {notes.length > 0 && (
        <ul className="space-y-1.5 mb-2">
          {notes.map((n, i) => (
            <li key={i} className="text-xs text-amber-100/90 bg-black/30 rounded-lg px-2 py-1.5">
              <div className="text-[10px] text-amber-300/60">
                {(n.created_at || "").slice(0, 19)} • {n.author || "admin"}
              </div>
              <div className="whitespace-pre-wrap">{n.text}</div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }}
          placeholder="Add a private note (customer never sees this)..."
          className="flex-1 h-9 px-3 rounded-full bg-black/40 border border-white/10 text-xs text-white"
          data-testid={`admin-note-input-${orderNumber}`}
        />
        <button
          type="button"
          onClick={addNote}
          disabled={busy || !text.trim()}
          className="h-9 px-4 rounded-full bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 text-xs font-bold disabled:opacity-50"
          data-testid={`admin-note-add-${orderNumber}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// CUSTOMERS TAB
// ============================================================================
function CustomersTab() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [historyByPhone, setHistoryByPhone] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const path = `/admin/customers${debounced ? `?search=${encodeURIComponent(debounced)}` : ""}`;
  const { data, loading, error } = useAdminFetch(apiFetch, path);
  const customers = data?.items || [];

  const toggle = async (phone) => {
    if (expanded === phone) { setExpanded(null); return; }
    setExpanded(phone);
    if (!historyByPhone[phone]) {
      try {
        const res = await apiFetch(`/admin/customers/${encodeURIComponent(phone)}/orders`);
        const payload = await readJsonSafe(res);
        if (!res.ok) throw new Error(extractApiMessage(res, payload, "Could not load order history"));
        setHistoryByPhone((prev) => ({ ...prev, [phone]: payload?.items || [] }));
      } catch (err) {
        toast.error(err?.message || "Could not load order history");
      }
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-customers">
      <div className="admin-panel px-4 py-4 sm:px-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-2xl font-bold">{t("dashboard.orderCustomers")} ({data?.total ?? 0})</h2>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("dashboard.customerSearchPlaceholder", "Search by customer ID, phone, email, or name...")}
            className="admin-input h-9 ps-9 pe-3 text-xs w-72"
            data-testid="customers-search"
          />
        </div>
      </div>
      </div>

      {loading ? <Loading /> : error ? <ErrorState label={error} /> : customers.length === 0 ? <Empty label={debounced ? t("dashboard.noCustomerMatch", "No match for \"{query}\"").replace("{query}", debounced) : t("dashboard.noCustomersYet", "No customers yet")} /> : (
        <div className="space-y-2">
          {customers.map((c) => {
            const identity = extractCustomerIdentity(c);
            const verification = verificationLabel(identity.verification);
            const history = historyByPhone[c.phone];

            return (
            <article key={c.phone || identity.customerId || identity.email || identity.displayName} className="admin-panel-soft overflow-hidden" data-testid={`customer-${c.phone || identity.customerId || "unknown"}`}>
              <button
                type="button"
                onClick={() => c.phone && toggle(c.phone)}
                className="w-full p-4 flex items-center justify-between gap-3 hover:bg-white/[0.02] text-left"
                disabled={!c.phone}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-sm truncate">{identity.displayName || ADMIN_PLACEHOLDER}</div>
                    {identity.customerId && (
                      <span className="inline-flex rounded-full border border-[#F37021]/30 bg-[#F37021]/10 px-2 py-0.5 font-mono text-[10px] text-[#F7A76F]">
                        {identity.customerId}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-white/55">{identity.email || identity.phone || ADMIN_PLACEHOLDER}</div>
                  <div className="mt-1 text-xs text-white/40">{[identity.city, identity.country].filter(Boolean).join(" / ") || ADMIN_PLACEHOLDER}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold">${c.total_spent.toFixed(2)}</div>
                  <div className="text-[10px] text-white/40">{c.orders_count} order{c.orders_count !== 1 ? "s" : ""}</div>
                  <div className="text-[10px] text-white/30">{verification}</div>
                </div>
                {expanded === c.phone ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
              </button>

              {expanded === c.phone && (
                <div className="p-4 border-t border-white/5 bg-black/30 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                    <KV label="Customer ID" value={identity.customerId || ADMIN_PLACEHOLDER} mono />
                    <KV label="Name" value={identity.displayName || ADMIN_PLACEHOLDER} />
                    <KV label="Verification" value={verification} />
                    <KV label="Email" value={identity.email || ADMIN_PLACEHOLDER} />
                    <KV label="Phone" value={identity.phone || ADMIN_PLACEHOLDER} mono />
                    <KV label="Language" value={formatLanguageLabel(identity.language)} />
                    <KV label="Address" value={identity.address || ADMIN_PLACEHOLDER} />
                    <KV label="City" value={identity.city || ADMIN_PLACEHOLDER} />
                    <KV label="Country" value={identity.country || ADMIN_PLACEHOLDER} />
                  </div>

                  {Array.isArray(c.related_order_ids) && c.related_order_ids.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Related order IDs</div>
                      <div className="flex flex-wrap gap-1.5">
                        {c.related_order_ids.map((orderId) => (
                          <span key={orderId} className="rounded-full bg-white/5 px-2 py-1 font-mono text-[10px] text-white/65">{orderId}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Order history</div>
                  {!history ? <Loading small /> : history.length === 0 ? <Empty label="No orders" small /> : (
                    <div className="space-y-2">
                      {history.map((o) => (
                        <CustomerOrderDetails key={o.order_number} order={o} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </article>
          );})}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TRASH TAB ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â cancelled + soft-deleted orders with reasons
// ============================================================================
function TrashTab() {
  const { apiFetch } = useAdminAuth();
  const [tick, setTick] = useState(0);
  const { data, loading } = useAdminFetch(apiFetch, "/admin/orders-trash?include_cancelled=true", [tick]);
  const reload = () => setTick((n) => n + 1);
  const items = data?.items || [];

  const restore = async (orderNum) => {
    const res = await apiFetch(`/admin/orders/${orderNum}/restore`, { method: "POST" });
    if (res.ok) {
      toast.success(`Order ${orderNum} restored`);
      reload();
    } else {
      toast.error("Could not restore");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-trash">
      <div className="admin-panel px-4 py-4 sm:px-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold">Trash & Cancelled ({data?.total ?? 0})</h2>
        <button type="button" onClick={reload} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/5 hover:bg-white/10 text-xs">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <p className="text-xs text-white/40 mb-4">
        Orders never disappear. Cancelled orders and soft-deleted orders stay here forever with their reason, customer info, and date.
      </p>
      </div>

      {loading ? <Loading /> : items.length === 0 ? <Empty label="Trash is empty" /> : (
        <div className="space-y-2">
          {items.map((o) => {
            const isDeleted = !!o.deleted;
            return (
              <article key={o.order_number} className="admin-panel-soft p-4" data-testid={`trash-${o.order_number}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-[#F37021]">{o.order_number}</span>
                      <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${isDeleted ? "bg-red-500/10 text-red-400 border-red-500/30" : STATUS_STYLES.cancelled}`}>
                        {isDeleted ? "deleted" : "cancelled"}
                      </span>
                      <span className="text-xs text-white/40">{(o.created_at || "").slice(0, 19)}</span>
                    </div>
                    <div className="mt-1 text-sm text-white/80">{(o.customer?.full_name || o.customer?.email || o.customer?.phone || ADMIN_PLACEHOLDER)}{o.customer?.phone ? ` / ${o.customer.phone}` : ""}</div>
                    {(o.deleted_reason || o.cancellation_reason) && (
                      <div className="mt-2 text-[11px] text-white/70 bg-black/30 rounded-lg px-2 py-1.5 border border-white/5">
                        <b>{isDeleted ? "Delete reason" : "Cancellation reason"}:</b> {o.deleted_reason || o.cancellation_reason}
                      </div>
                    )}
                    {isDeleted && (
                      <div className="mt-1 text-[10px] text-white/40">
                        Deleted by {o.deleted_by || "admin"} at {formatAdminDate(o.deleted_at)}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold">${(o.total || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-white/40">{(o.items || []).length} item(s)</div>
                    {isDeleted && (
                      <button
                        type="button"
                        onClick={() => restore(o.order_number)}
                        className="mt-2 inline-flex items-center gap-1 h-7 px-3 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-[10px] font-bold"
                        data-testid={`trash-restore-${o.order_number}`}
                      >
                        <RotateCcw className="w-3 h-3" /> Restore
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Tiny presentational helpers
function Loading({ small = false }) {
  return (
    <div className={`${small ? "py-4" : "py-12"} text-center text-white/40`}>
      <div className={`admin-panel-soft inline-flex items-center gap-3 ${small ? "px-3 py-2" : "px-4 py-3"}`}>
        <Loader2 className="w-5 h-5 animate-spin inline-block text-[#F58D4A]" />
        <span className={`${small ? "text-xs" : "text-sm"} text-white/55`}>Loading admin data...</span>
      </div>
    </div>
  );
}

function Empty({ label, small = false }) {
  return (
    <div className={`admin-panel-soft text-center text-white/40 ${small ? "py-3 text-xs px-4" : "py-10 text-sm px-6"}`}>
      {label}
    </div>
  );
}

function ErrorState({ label, onRetry, small = false }) {
  return (
    <div className={`admin-panel-soft text-center ${small ? "px-4 py-3" : "px-6 py-8"}`}>
      <div className={`${small ? "text-xs" : "text-sm"} text-red-200`}>{label}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-full bg-white/5 px-3 text-xs transition hover:bg-white/10"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Retry
        </button>
      )}
    </div>
  );
}

// ============================================================================
// CUSTOMER ORDER DETAILS - full per-order breakdown inside Customers tab
// ============================================================================
function CustomerOrderDetails({ order: o }) {
  const [open, setOpen] = useState(false);
  const items = o.items || [];
  const isDeleted = !!o.deleted;
  const status = isDeleted ? "deleted" : (o.status || "new");
  const statusCls = isDeleted
    ? "bg-red-500/10 text-red-400 border-red-500/30"
    : STATUS_STYLES[o.status || "new"];
  const customer = extractOrderCustomer(o);
  const notes = customer.deliveryNotes || o.customer?.notes || o.customer?.customer_notes;

  return (
    <article className="bg-black/40 rounded-xl border border-white/5 overflow-hidden" data-testid={`customer-order-${o.order_number}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2.5 flex items-center justify-between gap-2 text-xs hover:bg-white/[0.03]"
      >
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className="font-mono text-[#F37021] font-bold">{o.order_number}</span>
          <span className={`inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${statusCls}`}>
            {status}
          </span>
          <span className="text-white/40">{(o.created_at || "").slice(0, 16)}</span>
          {o.payment_method && (
            <span className="text-[10px] uppercase tracking-wider text-white/40 bg-white/5 px-1.5 py-0.5 rounded">
              {o.payment_method}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span>{items.length} item{items.length !== 1 ? "s" : ""} • <b className="text-white">${(o.total || 0).toFixed(2)}</b></span>
          {open ? <ChevronUp className="w-3.5 h-3.5 text-white/40" /> : <ChevronDown className="w-3.5 h-3.5 text-white/40" />}
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 border-t border-white/5 pt-3 space-y-3 text-xs">
          {/* Items list */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Products</div>
            <ul className="space-y-1.5">
              {items.map((it, idx) => {
                const imgs = Array.isArray(it.images) ? it.images : (it.image ? [it.image] : []);
                const cover = imgs[0] ? resolveAssetUrl(imgs[0]) : null;
                return (
                  <li key={`${o.order_number}-${idx}`} className="flex items-start gap-2.5 bg-black/40 rounded-lg p-2 border border-white/5">
                    {cover ? (
                      <img src={cover} alt="" className="w-12 h-12 rounded-md object-cover border border-white/10 shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-md bg-white/5 shrink-0 flex items-center justify-center text-white/30">
                        <Package className="w-4 h-4" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono text-[10px] text-[#F37021]">#{it.product_id || "?"}</span>
                        <span className="text-white/90 font-semibold truncate">{it.name || ADMIN_PLACEHOLDER}</span>
                      </div>
                      <div className="text-[10px] text-white/50 flex gap-2 flex-wrap mt-0.5">
                        <span>qty {it.quantity || 1}</span>
                        {it.size && <span>• size {it.size}</span>}
                        {(it.color_display || it.color) && <span>• color {it.color_display || it.color}</span>}
                        <span className="text-white/70">@ ${(it.unit_price ?? it.original_price ?? 0).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="text-right text-white/80 font-semibold shrink-0">
                      ${((it.line_total ?? (it.unit_price || 0) * (it.quantity || 1)) || 0).toFixed(2)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Totals breakdown */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <KV label="Subtotal" value={`$${(o.subtotal ?? 0).toFixed(2)}`} />
            <KV label="Delivery / Shipping" value={`$${(o.shipping_fee ?? o.shipping ?? 0).toFixed(2)}`} />
            <KV label="Total" value={`$${(o.total ?? 0).toFixed(2)}`} highlight />
            <KV label="Payment method" value={o.payment_method || ADMIN_PLACEHOLDER} />
            <KV label="Status" value={status} />
            <KV label="Order date" value={(o.created_at || "").slice(0, 19)} />
          </div>

          {/* Customer + delivery */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <KV label="Customer ID" value={customer.customerId || ADMIN_PLACEHOLDER} mono />
            <KV label="Customer" value={customer.displayName || ADMIN_PLACEHOLDER} />
            <KV label="Email" value={customer.email || ADMIN_PLACEHOLDER} />
            <KV label="Phone" value={customer.phone || ADMIN_PLACEHOLDER} mono />
            <KV label="Verification" value={verificationLabel(customer.verification)} />
            <KV label="Language" value={formatLanguageLabel(customer.language)} />
            <KV label="City" value={customer.city || ADMIN_PLACEHOLDER} />
            <KV label="Country" value={customer.country || ADMIN_PLACEHOLDER} />
            <KV label="Address" value={customer.address || ADMIN_PLACEHOLDER} />
          </div>

          {notes && (
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-2">
              <div className="text-[10px] uppercase tracking-wider text-blue-300 font-bold mb-0.5">Customer notes</div>
              <div className="text-white/80 whitespace-pre-wrap">{notes}</div>
            </div>
          )}

          {Array.isArray(o.admin_notes) && o.admin_notes.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1">Admin notes (private)</div>
              <ul className="space-y-1">
                {o.admin_notes.map((n, i) => (
                  <li key={i} className="text-white/80">
                    <span className="text-[10px] text-amber-300/70">{(n.created_at || "").slice(0, 19)} • {n.author || "admin"}</span>
                    <div className="whitespace-pre-wrap">{n.text}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(o.cancellation_reason || o.deleted_reason) && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-red-200">
              <div className="text-[10px] uppercase tracking-wider text-red-300 font-bold mb-0.5">
                {isDeleted ? "Delete reason" : "Cancellation reason"}
              </div>
              <div>{o.deleted_reason || o.cancellation_reason}</div>
              {isDeleted && (
                <div className="text-[10px] text-red-300/60 mt-1">
                  Deleted by {o.deleted_by || "admin"} at {(o.deleted_at || "").slice(0, 19)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function KV({ label, value, mono = false, highlight = false }) {
  return (
    <div className={`rounded-lg p-2 border ${highlight ? "bg-[#F37021]/10 border-[#F37021]/30" : "bg-black/40 border-white/5"}`}>
      <div className="text-[9px] uppercase tracking-wider text-white/40">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${highlight ? "text-white font-bold" : "text-white/90"} text-xs truncate`}>{value}</div>
    </div>
  );
}

// ============================================================================
// MISSING DATA TAB ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â products with incomplete info
// ============================================================================
const ISSUE_LABELS = {
  no_image: "No image",
  no_price: "No price",
  no_category: "No category",
  no_description: "No description",
  no_stock: "Out of stock",
};
const ISSUE_COLORS = {
  no_image: "bg-red-500/20 text-red-300 border-red-500/30",
  no_price: "bg-amber-500/20 text-amber-200 border-amber-500/30",
  no_category: "bg-purple-500/20 text-purple-200 border-purple-500/30",
  no_description: "bg-sky-500/20 text-sky-200 border-sky-500/30",
  no_stock: "bg-orange-500/20 text-orange-200 border-orange-500/30",
};

function MissingDataTab() {
  const { apiFetch } = useAdminAuth();
  const { data, loading } = useAdminFetch(apiFetch, "/admin/products/missing-data");
  const [filter, setFilter] = useState("");
  const counts = data?.counts || {};

  const items = useMemo(() => {
    const all = data?.items || [];
    if (!filter) return all;
    return all.filter((p) => p.issues.includes(filter));
  }, [data, filter]);

  return (
    <div data-testid="admin-missing">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-2xl font-bold">Missing Data ({data?.total ?? 0})</h2>
      </div>
      <p className="text-xs text-white/40 mb-3">Products that are missing critical info. Fix them in the Products tab to clear these alerts.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterChip active={!filter} onClick={() => setFilter("")} label={`All (${data?.total ?? 0})`} />
        {Object.keys(ISSUE_LABELS).map((k) => (
          <FilterChip
            key={k}
            active={filter === k}
            onClick={() => setFilter((p) => (p === k ? "" : k))}
            label={`${ISSUE_LABELS[k]} (${counts[k] || 0})`}
            tone={ISSUE_COLORS[k]}
          />
        ))}
      </div>

      {loading ? <Loading /> : items.length === 0 ? <Empty label="Nothing missing." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {items.map((p) => (
            <article key={p.id} className="bg-[#161616] rounded-xl border border-white/5 p-3 flex items-start gap-3" data-testid={`missing-product-${p.id}`}>
              {p.image ? (
                <img src={resolveAssetUrl(p.image)} alt="" className="w-14 h-14 rounded-lg object-cover border border-white/10 shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-red-500/10 border border-red-500/30 shrink-0 flex items-center justify-center text-red-400">
                  <Package className="w-5 h-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[10px] text-[#F37021]">#{p.id}</span>
                  <span className="text-sm font-semibold text-white/90 truncate">{p.name}</span>
                </div>
                <div className="text-[10px] text-white/50 mt-0.5">
                  {p.category || "-"} • ${p.originalPrice || 0} • stock {p.stock_qty}
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {p.issues.map((i) => (
                    <span key={i} className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${ISSUE_COLORS[i]}`}>
                      {ISSUE_LABELS[i]}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, tone = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-wider font-bold border transition-colors ${
        active ? "bg-[#F37021] text-white border-[#F37021]" : (tone || "bg-white/5 text-white/60 border-white/10 hover:bg-white/10")
      }`}
    >
      {label}
    </button>
  );
}

// ============================================================================
// PAYMENTS TAB ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â enable / disable OMT, Whish, COD
// ============================================================================
function PaymentsTab() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [methods, setMethods] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch("/admin/payment-methods");
        const d = await res.json();
        if (alive) setMethods(d.methods || []);
      } catch {
        toast.error(t("dashboard.paymentLoadFailed", "Could not load payment methods"));
      }
    })();
    return () => { alive = false; };
  }, [apiFetch]);

  const toggle = async (id, enabled) => {
    const next = methods.map((m) => (m.id === id ? { ...m, enabled } : m));
    // Optimistic guard: don't allow disabling the last one client-side either.
    if (!next.some((m) => m.enabled)) {
      toast.error(t("dashboard.paymentAtLeastOne", "At least one payment method must remain enabled."));
      return;
    }
    setSaving(true);
    setMethods(next);
    try {
      const res = await apiFetch("/admin/payment-methods", {
        method: "PUT",
        body: JSON.stringify({ methods: [{ id, enabled }] }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Failed");
      setMethods(d.methods || next);
      toast.success(`${id.toUpperCase()} ${enabled ? t("dashboard.enabled", "enabled") : t("dashboard.disabled", "disabled")}`);
    } catch (err) {
      toast.error(err?.message || t("dashboard.saveFailed", "Save failed"));
      // revert
      setMethods((prev) => prev.map((m) => (m.id === id ? { ...m, enabled: !enabled } : m)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-payments">
      <h2 className="font-display text-2xl font-bold mb-2">{t("dashboard.paymentMethods", "Payment Methods")}</h2>
      <p className="text-xs text-white/40 mb-4">
        {t("dashboard.paymentMethodsBody", "Toggle which methods customers see at checkout. Changes apply immediately to the website, mobile app, and order email.")}
      </p>

      {methods === null ? <Loading /> : (
        <div className="space-y-2 max-w-2xl">
          {methods.map((m) => (
            <article key={m.id} className="bg-[#161616] rounded-xl border border-white/5 p-4 flex items-center gap-4" data-testid={`payment-method-${m.id}`}>
              <div className="w-12 h-12 rounded-xl bg-[#F37021]/10 border border-[#F37021]/30 flex items-center justify-center shrink-0">
                <CreditCard className="w-5 h-5 text-[#F37021]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm">{m.label?.en || m.id}</div>
                <div className="text-xs text-white/50 mt-0.5 truncate">{m.description?.en || ""}</div>
              </div>
              <button
                type="button"
                onClick={() => toggle(m.id, !m.enabled)}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-bold border transition-colors disabled:opacity-50 ${
                  m.enabled
                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30"
                    : "bg-white/5 text-white/40 border-white/10 hover:bg-white/10"
                }`}
                data-testid={`payment-toggle-${m.id}`}
              >
                {m.enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {m.enabled ? t("dashboard.enabled", "Enabled") : t("dashboard.disabled", "Disabled")}
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LOGS TAB ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â append-only audit trail
// ============================================================================
const KIND_TONES = {
  product: "bg-purple-500/20 text-purple-200 border-purple-500/30",
  order: "bg-emerald-500/20 text-emerald-200 border-emerald-500/30",
  stock: "bg-amber-500/20 text-amber-200 border-amber-500/30",
  payment: "bg-blue-500/20 text-blue-200 border-blue-500/30",
  shipping: "bg-sky-500/20 text-sky-200 border-sky-500/30",
  warehouse: "bg-pink-500/20 text-pink-200 border-pink-500/30",
  content: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-500/30",
  settings: "bg-indigo-500/20 text-indigo-200 border-indigo-500/30",
};

function LogsTab() {
  const { apiFetch } = useAdminAuth();
  const { t, lang } = useAdminI18n();
  const [kind, setKind] = useState("");
  const [source, setSource] = useState("");
  const [severity, setSeverity] = useState("");
  const [result, setResult] = useState("");
  const [actionQ, setActionQ] = useState("");
  const [debouncedAction, setDebouncedAction] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAction(actionQ), 250);
    return () => clearTimeout(t);
  }, [actionQ]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (kind) p.set("item_kind", kind);
    if (source) p.set("source", source);
    if (severity) p.set("severity", severity);
    if (result) p.set("result", result);
    if (debouncedAction) p.set("action", debouncedAction);
    p.set("limit", "100");
    return p.toString();
  }, [kind, source, severity, result, debouncedAction]);

  const { data, loading } = useAdminFetch(apiFetch, `/admin/logs?${qs}`, [tick]);
  const items = data?.items || [];
  const kinds = data?.filters?.item_kinds || [];
  const sources = data?.filters?.sources || [];
  const severities = data?.filters?.severities || [];
  const results = data?.filters?.results || [];

  return (
    <div data-testid="admin-logs">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-2xl font-bold">{t("dashboard.adminLogs", "Admin Logs")} ({data?.total ?? 0})</h2>
        <button type="button" onClick={() => setTick((n) => n + 1)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/5 hover:bg-white/10 text-xs">
          <RefreshCw className="w-3.5 h-3.5" /> {t("dashboard.refresh")}
        </button>
      </div>
      <p className="text-xs text-white/40 mb-3">{t("dashboard.adminLogsBody", "Every important admin action is recorded here automatically (append-only). Use the filters to find specific changes.")}</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <FilterChip active={!kind} onClick={() => setKind("")} label={t("dashboard.allKinds", "All kinds")} />
        {kinds.map((k) => (
          <FilterChip key={k} active={kind === k} onClick={() => setKind((p) => (p === k ? "" : k))} label={humanizeKey(k)} tone={KIND_TONES[k] || ""} />
        ))}
        <select value={source} onChange={(e) => setSource(e.target.value)} className="h-9 rounded-full border border-white/10 bg-white/5 px-3 text-xs text-white">
          <option value="">{t("dashboard.allSources", "All sources")}</option>
          {sources.map((s) => <option key={s} value={s}>{humanizeKey(s)}</option>)}
        </select>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="h-9 rounded-full border border-white/10 bg-white/5 px-3 text-xs text-white">
          <option value="">{t("dashboard.allSeverity", "All severity")}</option>
          {severities.map((s) => <option key={s} value={s}>{humanizeKey(s)}</option>)}
        </select>
        <select value={result} onChange={(e) => setResult(e.target.value)} className="h-9 rounded-full border border-white/10 bg-white/5 px-3 text-xs text-white">
          <option value="">{t("dashboard.allResults", "All results")}</option>
          {results.map((s) => <option key={s} value={s}>{humanizeKey(s)}</option>)}
        </select>
        <input
          value={actionQ}
          onChange={(e) => setActionQ(e.target.value)}
          placeholder={t("dashboard.logsActionPlaceholder", "Filter by action (e.g. order.status, product.update)...")}
          className="h-9 px-3 rounded-full bg-white/5 border border-white/10 text-xs text-white w-72"
          data-testid="logs-action-filter"
        />
      </div>

      {loading ? <Loading /> : items.length === 0 ? <Empty label={t("dashboard.noLogsYet", "No logs yet")} /> : (
        <div className="space-y-1.5">
          {items.map((l) => {
            const beforeRows = summarizeObject(l.old, lang);
            const afterRows = summarizeObject(l.new, lang);
            const metaRows = summarizeObject(l.meta, lang);
            return (
              <article key={l.id} className="bg-[#161616] rounded-xl border border-white/5 p-3" data-testid={`log-${l.id}`}>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className={`inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${KIND_TONES[l.item_kind] || "bg-white/5 text-white/60 border-white/10"}`}>
                    {humanizeKey(l.item_kind)}
                  </span>
                  <span className="font-bold text-[#F37021]">{friendlyActionName(l.action, lang)}</span>
                  {l.source && <span className="rounded-full border border-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white/45">{humanizeKey(l.source)}</span>}
                  {l.severity && <span className="rounded-full border border-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white/45">{humanizeKey(l.severity)}</span>}
                  {l.result && <span className="rounded-full border border-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-white/45">{humanizeKey(l.result)}</span>}
                  {l.item_id && <span className="text-white/50">{t("dashboard.on", "on")} <span className="font-mono text-white/80">{l.item_id}</span></span>}
                  <span className="text-white/40">{t("dashboard.by", "by")} {l.actor}{l.actor_role ? ` (${humanizeKey(l.actor_role)})` : ""}</span>
                  <span className="text-white/40 ms-auto">{(l.created_at || "").slice(0, 19)}</span>
                </div>
                {(beforeRows.length > 0 || afterRows.length > 0) && (
                  <div className="mt-2 grid grid-cols-1 gap-2 text-[11px] sm:grid-cols-2">
                    {beforeRows.length > 0 && (
                      <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-2 text-red-100">
                        <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-red-300">{t("dashboard.before", "Before")}</span>
                        <div className="space-y-1">
                          {beforeRows.map((row) => (
                            <div key={`${l.id}-old-${row.label}`} className="flex justify-between gap-3">
                              <span className="text-red-100/55">{row.label}</span>
                              <span className="text-right text-red-50/90">{row.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {afterRows.length > 0 && (
                      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2 text-emerald-100">
                        <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-emerald-300">{t("dashboard.after", "After")}</span>
                        <div className="space-y-1">
                          {afterRows.map((row) => (
                            <div key={`${l.id}-new-${row.label}`} className="flex justify-between gap-3">
                              <span className="text-emerald-100/55">{row.label}</span>
                              <span className="text-right text-emerald-50/90">{row.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {metaRows.length > 0 && (
                  <div className="mt-2 rounded-lg border border-white/5 bg-white/[0.02] p-2 text-[11px] text-white/60">
                    <span className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-white/35">{t("dashboard.details", "Details")}</span>
                    <div className="grid gap-1 sm:grid-cols-2">
                      {metaRows.map((row) => (
                        <div key={`${l.id}-meta-${row.label}`} className="flex justify-between gap-3">
                          <span>{row.label}</span>
                          <span className="text-right text-white/80">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// SHEIN CATEGORIES TAB  ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â add / edit / hide / delete / reorder
// ============================================================================
function SheinCategoriesTab() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);    // category being edited
  const [creating, setCreating] = useState(false);
  const [dragId, setDragId] = useState(null);

  const reload = async () => {
    try {
      const res = await apiFetch("/admin/shein-categories?include_hidden=true");
      const data = await res.json();
      setItems((data.items || []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    } catch { toast.error(t("dashboard.sheinLoadFailed", "Could not load SHEIN categories")); }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (payload, id) => {
    const method = id ? "PUT" : "POST";
    const url = id ? `/admin/shein-categories/${id}` : "/admin/shein-categories";
    try {
      const res = await apiFetch(url, { method, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || t("dashboard.saveFailed", "Save failed"));
      toast.success(id ? t("dashboard.updated", "Updated") : t("dashboard.created", "Created"));
      setEditing(null);
      setCreating(false);
      reload();
    } catch (e) { toast.error(e?.message || t("dashboard.saveFailed", "Save failed")); }
  };

  const toggleHidden = async (cat) => {
    await save({ name: cat.name, sort_order: cat.sort_order, is_hidden: !cat.is_hidden }, cat.id);
  };

  const remove = async (cat) => {
    if (!window.confirm(t("dashboard.sheinDeleteConfirm", "Delete SHEIN category \"{name}\"? Products already in this category will keep their tag, but the chip disappears from the storefront.").replace("{name}", cat.name?.en || cat.id))) return;
    try {
      const res = await apiFetch(`/admin/shein-categories/${cat.id}`, { method: "DELETE" });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail || t("dashboard.deleteFailed", "Delete failed"));
      }
      toast.success(t("dashboard.deleted", "Deleted"));
      reload();
    } catch (e) { toast.error(e?.message || t("dashboard.deleteFailed", "Delete failed")); }
  };

  const handleDragStart = (id) => setDragId(id);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return;
    const order = items.map((c) => c.id);
    const from = order.indexOf(dragId);
    const to = order.indexOf(targetId);
    if (from === -1 || to === -1) return;
    order.splice(from, 1);
    order.splice(to, 0, dragId);
    // Optimistic re-render
    const reordered = order.map((id) => items.find((c) => c.id === id)).filter(Boolean)
      .map((c, i) => ({ ...c, sort_order: i }));
    setItems(reordered);
    setDragId(null);
    try {
      await apiFetch("/admin/shein-categories/reorder", { method: "POST", body: JSON.stringify({ order }) });
      toast.success(t("dashboard.orderSaved", "Order saved"));
    } catch (e) {
      toast.error(t("dashboard.reorderFailed", "Reorder failed - refresh"));
      reload();
    }
  };

  return (
    <div data-testid="admin-shein">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-2xl font-bold">{t("dashboard.sheinCategories", "SHEIN Categories")} ({items?.length ?? 0})</h2>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#F37021] hover:bg-[#E06015] text-xs font-bold text-white"
          data-testid="shein-cat-new"
        >
          <Plus className="w-3.5 h-3.5" /> {t("dashboard.addCategory", "Add category")}
        </button>
      </div>
      <p className="text-xs text-white/40 mb-4">{t("dashboard.sheinCategoriesBody", "Drag rows to reorder. Hidden categories disappear from the storefront / mobile app but stay attached to existing products.")}</p>

      {items === null ? <Loading /> : items.length === 0 ? <Empty label={t("dashboard.noCategoriesYet", "No categories yet")} /> : (
        <ul className="space-y-2 max-w-3xl">
          {items.map((c) => (
            <li
              key={c.id}
              draggable
              onDragStart={() => handleDragStart(c.id)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(c.id)}
              className={`bg-[#161616] rounded-2xl border p-3 flex flex-wrap items-center gap-2 sm:gap-3 ${c.is_hidden ? "border-white/5 opacity-50" : "border-white/10"} ${dragId === c.id ? "ring-2 ring-[#F37021]" : ""}`}
              data-testid={`shein-cat-${c.id}`}
            >
              <GripVertical className="w-4 h-4 text-white/30 shrink-0 cursor-grab" />
              <span className="font-mono text-[10px] text-[#F37021] shrink-0">#{c.sort_order ?? "-"}</span>
              <div className="min-w-0 flex-1 basis-full sm:basis-0">
                <div className="text-sm font-semibold">{c.name?.en || c.id}</div>
                <div className="text-[11px] text-white/50 truncate">
                  AR: {c.name?.ar || <i className="text-white/30">-</i>} · FR: {c.name?.fr || <i className="text-white/30">-</i>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ms-auto">
                <button type="button" onClick={() => toggleHidden(c)} className="h-7 w-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid={`shein-cat-hide-${c.id}`} title={c.is_hidden ? t("dashboard.show", "Show") : t("dashboard.hide", "Hide")}>
                  {c.is_hidden ? <EyeOff className="w-3.5 h-3.5 text-red-400" /> : <Eye className="w-3.5 h-3.5 text-emerald-400" />}
                </button>
                <button type="button" onClick={() => setEditing(c)} className="h-7 px-3 rounded-full bg-white/5 hover:bg-white/10 text-[10px] font-bold uppercase tracking-wider" data-testid={`shein-cat-edit-${c.id}`}>{t("dashboard.edit", "Edit")}</button>
                <button type="button" onClick={() => remove(c)} className="h-7 px-3 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 text-[10px] font-bold uppercase tracking-wider" data-testid={`shein-cat-delete-${c.id}`}>{t("dashboard.delete", "Delete")}</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(creating || editing) && (
        <SheinCategoryForm
          existing={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSave={(payload, id) => save(payload, id)}
        />
      )}
    </div>
  );
}

function SheinCategoryForm({ existing, onClose, onSave }) {
  const { t } = useAdminI18n();
  const isEdit = !!existing;
  const [en, setEn] = useState(existing?.name?.en || "");
  const [ar, setAr] = useState(existing?.name?.ar || "");
  const [fr, setFr] = useState(existing?.name?.fr || "");
  const [id, setId] = useState(existing?.id || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!en.trim()) { toast.error(t("dashboard.englishNameRequired", "English name is required")); return; }
    setBusy(true);
    await onSave({ id: id || undefined, name: { en, ar, fr } }, isEdit ? existing.id : null);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" data-testid="shein-cat-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-[#141414] rounded-2xl border border-white/10 shadow-2xl my-4 sm:my-8">
        <header className="flex items-center justify-between p-4 sm:p-5 border-b border-white/5">
          <h3 className="font-display text-base sm:text-lg font-bold">{isEdit ? t("dashboard.editNamed", "Edit \"{name}\"").replace("{name}", existing.name?.en || existing.id) : t("dashboard.newSheinCategory", "New SHEIN category")}</h3>
          <button type="button" onClick={onClose} className="text-white/40 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-4 sm:p-5 space-y-3">
          {!isEdit && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t("dashboard.idSlugOptional", "ID (slug) - optional, auto-derived from EN name")}</div>
              <input value={id} onChange={(e) => setId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))} className="form-input" placeholder="e.g. women_wear" data-testid="shein-cat-id" />
            </label>
          )}
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t("dashboard.nameEn", "Name EN *")}</div>
            <input value={en} onChange={(e) => setEn(e.target.value)} className="form-input" required data-testid="shein-cat-en" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t("dashboard.nameAr", "Name AR")}</div>
            <input value={ar} onChange={(e) => setAr(e.target.value)} className="form-input" data-testid="shein-cat-ar" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{t("dashboard.nameFr", "Name FR")}</div>
            <input value={fr} onChange={(e) => setFr(e.target.value)} className="form-input" data-testid="shein-cat-fr" />
          </label>
        </div>
        <footer className="flex items-center justify-end gap-2 p-4 sm:p-5 border-t border-white/5">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold">{t("dashboard.cancel", "Cancel")}</button>
          <button type="submit" disabled={busy} className="h-9 px-5 rounded-full bg-[#F37021] hover:bg-[#E06015] text-white text-xs font-bold disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="shein-cat-save">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isEdit ? t("dashboard.save", "Save") : t("dashboard.create", "Create")}
          </button>
        </footer>
      </form>
    </div>
  );
}
